// useRoomChrome — Classic / Modern 両レイアウトが共有する「描画 chrome の state +
// ハンドラ」を1箇所にまとめた hook.
//
// 背景 (docs/_wip/modern-layout.md §9 実装方針「フロート事前準備」/ §9.4):
//   - レイアウトは純粋なクライアント表示の差。engine / layers / protocol は
//     レイアウト非依存で1本。よって ColorWheelPalette / ArgbSliderPanel / LayerPopup /
//     ToolPanel / PalettePanel / MaskPanel 等の Classic 部品は「配置先に依存しない
//     再配置可能ユニット」であり、それらに流す state とハンドラは **レイアウト共通の
//     concern** として Provider/hooks でレイアウト外に置くのが筋 (= 共通 concern を
//     dock/float のどちらに置いても壊れない土台)。
//   - この hook はその共通 concern を集約する。ModernLayout はこれを使って Classic
//     部品を §9 案B 骨格のドックに再配置する (= 描画能力は増やさない、配置だけ変える)。
//
// なぜ ClassicLayout から「抽出」ではなく「並立」なのか (重要な設計判断):
//   - ClassicLayout.tsx には大量の source-string smoke test (ClassicLayout.smoke.test.ts)
//     が貼り付いており、行を移動すると回帰検知が壊れる。`feature/modern-layout-shell`
//     の確定スコープは「Classic を破壊改変せず」(§9.4.1 ⑤ 周辺) なので、本スライスでは
//     ClassicLayout には触れず、共通ロジックをこの hook に **複製** して ModernLayout が
//     使う。ClassicLayout と本 hook の重複は意図的な暫定 (= シェル投入を安全に検証する
//     ためのトレードオフ)。Classic を本 hook 利用へ寄せて重複を解消するのは **後続
//     スライス** に送る (smoke test の貼り替えを伴うため別作業)。
//
// レイアウト固有の concern (= dock/grid・fullscreen・pan splitter・chatDetached・
// topbar) はこの hook に入れない。各レイアウトが自分で持つ。

import { hasPerm, Perm, type StrokePen, type TexturePreset } from '@oekaki/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CAPTURE_EXPORT_TIMEOUT_MS,
  CAPTURE_FAILED_MESSAGE,
  captureFileName,
  withTimeout,
} from '../../../application/captureShare.js';
import {
  createPenPressureStore,
  type PenPressureSettings,
} from '../../../application/penPressure.js';
import { canClearWhiteboard } from '../../../application/uiFeedback.js';
import { DEFAULT_PEN_CONFIG, type PenConfig } from '../../../domain/PenConfig.js';
import { DEFAULT_PEN_FOR_TAB, type PenTabId, tabForPen } from '../../../domain/PenTabs.js';
import type { RoomState } from '../../../domain/RoomState.js';
import {
  createAaPreferenceStore,
  resolveEffectiveAa,
  seedAaForFirstVisitor,
} from '../../../infrastructure/aaPreference.js';
import {
  type BrushCursorType,
  createBrushCursorPreferenceStore,
} from '../../../infrastructure/brushCursorPreference.js';
import { createDotByDotPreferenceStore } from '../../../infrastructure/dotByDotPreference.js';
import {
  type CanvasDrawingEngine,
  type NameLabel,
  ZOOM_MIN,
} from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
// 2026-09-29 viewport transform 移行 (決定5、modern-phone-shell-2026-07-02.md §6):
// scroll ベースの pan/zoom-anchor 補正 (旧 `../classic/zoomScroll.js`) を
// `{tx,ty}` 座標変換モデルへ置き換える。純粋ロジックは `infrastructure/drawing/
// viewport.ts`、DOM 適用 (padding 実測 / transform 書き込み) もそこに集約した
// 薄いヘルパを使う。
import {
  applyViewportTransform,
  type CanvasViewMapper,
  createViewMapper,
  initialViewport,
  measureSlot,
  measureSlotOrigin,
  ORIGIN_VIEWPORT,
  snapOffsetToDevicePixel,
  type ViewportOffset,
  type ViewTransform,
  viewportAfterFlipToggleRotated,
  viewportAfterRotation,
  viewportAfterZoomRotated,
  visibleCanvasQuad,
} from '../../../infrastructure/drawing/viewport.js';
// キャンバス表示回転 (canvas-rotation 段取り 2): 角度の純粋ロジック。
import {
  normalizeRotation,
  ROTATION_STEP_DEG,
} from '../../../infrastructure/drawing/viewRotation.js';
// M1-a 分数ズーム (modern-phone-shell-2026-07-02.md §3.1) + 決定2 差し替え
// (consult 2026-09-29、§6): ± ボタン / ホイールのステップは CSP 方式 (×/÷1.1
// + アンカースナップ、zoom.ts) と fit-to-viewport 計算の純粋ポリシー。
import {
  clampZoom,
  computeFitZoom,
  type DotByDotMode,
  isDotByDotDpr,
  zoomStepIn,
  zoomStepOut,
} from '../../../infrastructure/drawing/zoom.js';
import {
  createLayerVisibilityStore,
  visibilityMapOf,
} from '../../../infrastructure/layerVisibilityPreference.js';
import { createRegionNewBadgeSeenStore } from '../../../infrastructure/regionNewBadgeSeen.js';
import type { VisitorKind } from '../../../infrastructure/returningVisitor.js';
import { createShowOwnCustomCursorPreferenceStore } from '../../../infrastructure/showOwnCustomCursorPreference.js';
import {
  createSpuitSamplePreferenceStore,
  type SpuitSampleMode,
} from '../../../infrastructure/spuitSamplePreference.js';
import {
  createStabilizerPreferenceStore,
  type StabilizerLevel,
} from '../../../infrastructure/stabilizerPreference.js';
import { useImagepaste } from '../../hooks/useImagepaste.js';
import { type RegionMoveController, useRegionMove } from '../../hooks/useRegionMove.js';
import { useSession } from '../../hooks/useSession.js';
import { useConnectionLocked } from '../../providers/ConnectionLockContext.js';
import {
  applyWidthToActiveBucket,
  isEraserActive,
  resolveActiveWidth,
  type WidthBuckets,
} from '../classic/eraserWidth.js';
import { defaultActiveLayerId, initialLayerVisibility } from '../classic/layerPopup.js';
import { type MaskSlotValue, type MaskTypeUI, slotsToMask } from '../classic/maskHelpers.js';
import { LEGACY_PALETTE } from '../classic/PalettePanel.js';
import { DEFAULT_ERASER_PEN_WIDTHS, DEFAULT_PEN_WIDTHS } from '../classic/penWidthSlider.js';
import type { LayerOpacityControl } from './layerOpacity.js';
import type { LayerThumbnailHub } from './layerThumbnailHub.js';
import { useLayerOpacity } from './useLayerOpacity.js';
import { useLayerThumbnails } from './useLayerThumbnails.js';

/**
 * canvas 背景色の MVP default (= 不透明白 rgb24)。protocol の
 * DEFAULT_SNAPSHOT_BACKGROUND.rgb と一致 (snapshot.ts:83)。engine に getBackground()
 * が無いので、engine 差し替え (新規 join) 時はこの値で seed し、persisted bg は
 * snapshot meta 経由の onBackgroundChange seam で上書きする。
 */
const DEFAULT_BACKGROUND_RGB = 0xffffff;

/**
 * 背景色 send の trailing debounce (canvas-background-color §4.3)。ColorWheelPalette
 * の wheel ドラッグ毎フレーム発火を「落ち着いた所で 1 発」に集約するための猶予。
 */
const BACKGROUND_SET_DEBOUNCE_MS = 250;

/**
 * 背景色 send の ack 待ちタイムアウト (fix/timelapse-background Bug D)。
 *
 * `sendBackgroundSet` は fire-and-forget — 成功は自分にも返ってくる
 * `whiteboard.background-set` broadcast (= `onBackgroundChange` seam) でしか
 * 分からない。失敗経路は 2 つあるが、どちらも **この send を狙って** 拒否/破棄
 * されたと確実には識別できない:
 *   - WS 未接続 (再接続中) → `WSClient.send` が `send-before-open` を emit する
 *     だけで、どのペイロードだったかの情報は無い。
 *   - サーバ拒否 (権限なし 等) → 本人に `system.error` unicast が飛ぶが、
 *     `code`/`message` だけで元イベントと紐付く requestId が無い — 同時に
 *     他の操作 (clear 等) を行っていれば取り違える。
 *
 * 妥協: この時間内に **自分の送った rgb と同じ値の** `onBackgroundChange` echo
 * が来なければ「届かなかった/拒否された」とみなしローカルプレビューを確定色へ
 * 戻す。個別の `system.error` を解析して即時判定することはしない (誤検知の
 * リスクの方が「最大 ~5秒 プレビューが誤ったままになる」より高いと判断)。
 * WS 切断検知 (`transport.onStatus`) はこれより先に即時リバートするので、
 * 実際にこのタイムアウトが効くのは「接続はしているがサーバが拒否した」ケース
 * だけになる。
 */
const BACKGROUND_SET_ACK_TIMEOUT_MS = 5_000;

/**
 * `useRoomChrome` の戻り値。Classic 部品に流す props を 1 オブジェクトに集約する。
 * (詳細な意味は各フィールドの利用箇所 = ClassicLayout / ModernLayout 参照。)
 */
export interface RoomChrome {
  // --- pen / color / width ---
  readonly pen: PenConfig;
  readonly paletteSlots: ReadonlyArray<number>;
  readonly selectedPaletteIdx: number;
  readonly activePenWidths: ReadonlyArray<number>;
  readonly activeSelectedPenWidthIndex: number;
  // texture spike (texture-brush-design.md §8): Modern の暫定テクスチャボタンが
  // `'texture'` を渡す。Classic ToolPanel は引き続き `'brush'|'flat'|'marker'` のみ
  // を渡す (= 渡し手の subset 利用なので互換)。
  readonly onSelectPen: (
    kind:
      | 'brush'
      | 'flat'
      | 'marker'
      | 'texture'
      | 'tone'
      | 'airbrush'
      | 'crayon'
      | 'pastel'
      | 'dot',
  ) => void;
  // texture-brush-design.md §10.1 / §11 step 3: Modern 2階層 UI の preset 選択。
  // `pen='texture'` のとき ModernToolProperties の preset サムネが呼ぶ。pen.texturePreset
  // に書き、以降の stroke が `penParams:{kind:'texture',preset}` を載せる (penParamsFor)。
  // pen='texture' でないときも値は保持される (= 次に texture を選んだとき直前 preset が復活)。
  // Classic は texture を出さないので呼ばない (= 既定 'paper' のまま無影響)。
  readonly texturePreset: TexturePreset;
  readonly onSelectTexturePreset: (preset: TexturePreset) => void;
  // texture-brush-design.md §12.2 段階1: Modern「トーン」カテゴリの番手 (density) スライダ。
  // `pen='tone'` のとき ModernToolProperties が呼ぶ。pen.toneDensity (0..1) に書き、以降の
  // stroke が `penParams:{kind:'tone',pattern:'dot',density}` を載せる (penParamsFor)。
  // pen='tone' でないときも値は保持される (= 次に tone を選んだとき直前 density が復活)。
  // Classic は tone を出さないので呼ばない (= 既定 0.5 のまま無影響)。
  readonly toneDensity: number;
  readonly onToneDensityChange: (density: number) => void;
  // airbrush-design-2026-08-15.md §1.3 / §4-2 / §6: grain (粒の粗さ 0..3) / density
  // (噴霧密度 0..1)。`pen='airbrush'` のとき `PenKindPicker` が内部でスライダを出す
  // (共有部品なので Classic PenPopup / Modern ModernToolProperties の両方に効く、
  // texture preset と同じ相乗り方式)。値は pen.airbrushGrain/airbrushDensity に書き、
  // penParamsFor が pen='airbrush' のときだけ wire/engine に流す。pen='airbrush' でなく
  // ても保持しておけば (= setPen で常に書く)、airbrush に戻したとき直前値が復活する。
  readonly airbrushGrain: number;
  readonly onAirbrushGrainChange: (grain: number) => void;
  readonly airbrushDensity: number;
  readonly onAirbrushDensityChange: (density: number) => void;
  // phase11-light-pens-2026-08-21.md §2.6 / §6: クレヨン/パステルの目の粗さ (0..2)。
  // `pen='crayon'|'pastel'` のとき `PenKindPicker`/`PenParamsSection` が内部で segmented
  // を出す (airbrush grain と同じ相乗り方式)。値は pen.crayonGrain/pastelGrain に書き、
  // penParamsFor が該当ペンのときだけ wire/engine に流す。
  readonly crayonGrain: number;
  readonly onCrayonGrainChange: (grain: number) => void;
  readonly pastelGrain: number;
  readonly onPastelGrainChange: (grain: number) => void;
  // phase11-light-pens-2026-08-21.md §4.5-§4.7: トーンのドット間隔 (pitch、8/16/32/64
  // の4段階、既定16)。封印解除に伴い toneDensity と同じ相乗り方式で追加。値は
  // pen.tonePitch に書き、penParamsFor が pen='tone' のときだけ wire/engine に流す。
  readonly tonePitch: number;
  readonly onTonePitchChange: (pitch: number) => void;
  // 同 §3.4/§3.5/§6: ドットの間隔 (spacing、1..200、掃引ステップ単位で px ではない)。
  // 値は pen.dotSpacing に書き、penParamsFor が pen='dot' のときだけ wire/engine に流す。
  readonly dotSpacing: number;
  readonly onDotSpacingChange: (spacing: number) => void;
  readonly onToggleEraser: () => void;
  // キーボードショートカット (B = brush 直接選択 / S = サインペン = onSelectPen('flat') /
  // E = eraser 直接選択) 用の direct mutator。Classic がインラインで持っていた `setPen`
  // 直叩き挙動を共有コントローラへ移設 (= engine state の単一 source-of-truth に統一)。
  readonly onSelectBrush: () => void;
  readonly onSelectEraserDirect: () => void;
  // 旧 M = マーカー bucket toggle (lastMarkerKind 機構)。marker (コピック) 段階撤去に伴い
  // 2026-06-12 に M→S=サインペン直接キー化でキーボード経路から外れた (= 参照無し)。
  // bucket 内部機構ごと marker 撤去 Stage X で撤去予定。それまでは挙動温存のため残置。
  readonly onMarkerBucketToggle: () => void;
  // --- CSP流 循環ショートカット (shortcut-settings.md「CSP の実モデル」2026-08-21 確定) ---
  /**
   * `tool.pen.*` 個別ペン action (ドット/トーン/エアブラシ/クレヨン/パステル/テクスチャ)
   * の直接選択に使う汎用 mutator。`onSelectBrush`/`onSelectEraserDirect` と同じ
   * `setPen({...p, pen})` パターンを任意の `StrokePen` に一般化したもの (= 挙動は等価、
   * 既存 2 つを置き換えはしない。keyboard hook からのみ呼ばれる想定)。
   */
  readonly onSelectPenRef: (pen: StrokePen) => void;
  /**
   * `tool.tab.*` タブ action (線画/塗り/質感) の「最後に使ったペンへ」を解決して選ぶ。
   * `lastUsedPenForTab` (下記) を参照する。
   */
  readonly onSelectTabRef: (tab: PenTabId) => void;
  /**
   * 循環ショートカットの「記憶した前回位置」(shortcut-settings.md「循環位置のリセットは
   * 退室・入室時のみ」)。この hook 自体が room session の寿命 (= 入室で mount・退室で
   * unmount) を持つので、`useState` の初期値 (空 object) がそのまま「入室時リセット」を
   * 満たす。key は `formatChordFromEvent` で正規化した binding 文字列。**Classic⇄Modern
   * のレイアウト切替では消えない** (= `useLayoutKeyboardShortcuts` は各 layout 内で
   * remount されるが、この state は chrome = レイアウト分岐より上位が持つため)。
   */
  readonly shortcutCyclePositions: Readonly<Record<string, number>>;
  readonly onShortcutCycleIndexChange: (binding: string, index: number) => void;
  readonly onWidthChange: (w: number) => void;
  readonly onPenWidthCellClick: (index: number) => void;
  readonly onPenWidthValueChange: (selectedIndex: number, newWidth: number) => void;
  readonly onColorChange: (c: number) => void;
  // スポイト色pick 専用の色採用 (spuit-sample-modes-2026-06-22 §2.1、モードB 対称トグル)。
  // CanvasStage が DrawingCanvas の onSpuitColor へ渡す。モードB で色を拾い、今が消しゴム
  // なら直前ペンへ復帰してから色採用。A/C はツール不変・色採用のみ。手動色変更 (onColorChange)
  // とは分離 (= ツール復帰はスポイト色pick 時のみ起きる)。
  readonly onSpuitColor: (c: number) => void;
  readonly onAlphaChange: (a: number) => void;
  readonly onPaletteSlotSelect: (idx: number) => void;
  /**
   * α feedback (2026-06-13): パレット 16 slot を初期色 (`LEGACY_PALETTE`) に戻す。
   * 選択中 slot は維持し、ペン色も選択中 slot の初期色に揃える (詳細は実装コメント参照)。
   * 呼び出し側 (PalettePanel / ModernColorPalette) が誤クリック防止の確認 UI を挟む。
   */
  readonly onPaletteReset: () => void;
  // --- mask ---
  readonly slot1: MaskSlotValue;
  readonly slot2: MaskSlotValue;
  readonly maskTypeUI: MaskTypeUI;
  readonly onMaskSlotClick: (slot: 0 | 1) => void;
  readonly onMaskTypeToggle: () => void;
  // --- pen pressure ---
  readonly currentPressure: PenPressureSettings;
  readonly onPressureChange: (next: PenPressureSettings) => void;
  // --- 手ぶれ補正 (stroke stabilization, §9.8 モディファイア) ---
  // Modern が UI 露出 (ModernToolProperties)。engine は §9.7 で共有・永続なので設定は
  // レイアウト非依存に効く。off/弱/中/強の per-user localStorage、default off。
  readonly stabilizerLevel: StabilizerLevel;
  readonly onStabilizerChange: (level: StabilizerLevel) => void;
  // --- アンチエイリアス (AA, §9.9 モディファイア + 表現パリティ §1.1) ---
  // 手ぶれ補正と違い engine ではなく **PenConfig.aa → stroke.start.aa の per-stroke
  // param** で運ぶ (§9.9 決定4)。tri-state (未設定 | on | off) の **実効値**: 明示設定が
  // あればレイアウト間で共有、未設定なら **共通既定 off** (= 旧 Java 互換の描き味、
  // consult 2026-06-13 続報でレイアウト別既定 Modern=on を撤廃 — レイアウト切替で線の
  // 出力が変わるのは「レイアウト = 見せ方の差」§9.4 と矛盾するため)。値は `pen.aa` に
  // 同期され、DrawingCanvas が wire/ローカル描画の両方に流す。UI は ModernToolProperties
  // トグル + 上部バー「ペン」→ PenSettingsModal (= 両レイアウト露出、consult 2026-06-13
  // 続報) の両方がこの値を読み書きする (二重の真実なし)。
  readonly aaEnabled: boolean;
  // 明示設定の単一経路。localStorage (oekaki:aa-enabled) へ永続化 = 以後「明示設定あり」。
  readonly onAaChange: (next: boolean) => void;
  // --- ドットバイドット表示 (canvas-dot-by-dot-2026-10-28.md) ---
  // 'auto' (既定) | 'off'。端末ごとの localStorage (oekaki:dot-by-dot)。engine へ反映。
  readonly dotByDotMode: DotByDotMode;
  readonly onDotByDotModeChange: (next: DotByDotMode) => void;
  // 今の画面 (dpr) が自動の対象か。UI の「この画面では効いています」表示用。
  readonly dotByDotActive: boolean;
  // --- ブラシカーソル種別 (consult 2026-06-21、α フィードバック 123 さん) ---
  // 'brush-ring' (既定): BrushCursorOverlay のリングを表示 + canvas は `cursor: none`。
  // 'arrow': リング非表示 + canvas は OS 標準カーソル (= 絵を一切隠さない、Java 版懐古)。
  // per-user localStorage (oekaki:brush-cursor-type) で永続。レイアウト非依存に効く
  // (CanvasStage が innerWrapper の data 属性 + BrushCursorOverlay の cursorType に流す)。
  // UI は両レイアウトの上部バー「表示」→ UserPreferencesModal のトグル。
  readonly brushCursorType: BrushCursorType;
  readonly onBrushCursorTypeChange: (next: BrushCursorType) => void;
  // --- 自分のカスタムカーソルを自分の画面にも表示するか (作者 staging フィードバック
  // 2026-09-19 follow-up) --- 既定 ON。OFF は自分の画面での表示だけに影響し、相手への
  // 配信・相手側の表示・参加者リストのサムネイルには影響しない。per-user localStorage
  // (oekaki:show-own-custom-cursor) で永続、レイアウト非依存。UI は
  // `PenSettingsPanel`「ブラシカーソル」の直下 + `CursorEditorPanel` 下部の同一 controlled
  // チェックボックス (両方が同じ設定を指す)。
  readonly showOwnCustomCursor: boolean;
  readonly onShowOwnCustomCursorChange: (next: boolean) => void;
  // --- スポイトのサンプル元モード (spuit-sample-modes-2026-06-22、consult 2026-06-22) ---
  // A 'display' (既定、全可視+bg、見たまま) / B 'layer' (選択レイヤー生ピクセル・bg無視、
  // α0で消しゴム切替・半透明はRGBのみ) / C 'layer-bg' (選択レイヤー+bg、旧挙動)。
  // per-user localStorage (oekaki:spuit-sample-mode) で永続、protocol 非依存。レイアウト
  // 非依存に効く (CanvasStage の DrawingCanvas が spuitSampleMode を engine.samplePixel に渡す)。
  // UI は両レイアウトの上部バー「表示」→ UserPreferencesModal の3択 <select>。
  readonly spuitSampleMode: SpuitSampleMode;
  readonly onSpuitSampleModeChange: (next: SpuitSampleMode) => void;
  // --- pen-only mode (palm rejection) ---
  readonly penOnlyMode: boolean;
  readonly setPenOnlyMode: (next: boolean) => void;
  readonly handlePenAutoEnable: () => void;
  // --- 手のひら (pan) モード (§9.7 Phase 2) ---
  // CanvasStage が scroll コンテナを所有するため、pan の有効/無効を chrome 経由で渡す。
  // Classic は spaceHeld||panToggleOn を sync、Modern は自前トグルから set する。
  // CanvasStage (RoomLayout 直下・chrome の兄弟) が panActive を読んでドラッグ pan を行う。
  readonly panActive: boolean;
  readonly setPanActive: (next: boolean) => void;
  // --- ストローク進行中シグナル (プレビュー負荷抑制用、feature/preview-perf 2026-06-13) ---
  // DrawingCanvas (CanvasStage 配下) が pointerdown→pointerup の間 true を通知する。
  // プレビュー (Classic PreviewOverlay / Modern ModernPreviewPanel) はこれ + panActive を
  // 見て「自分の操作中はサムネ再生成をサスペンド」する (= カーソルのフレーム予算を守る)。
  // React state ではなく ref 経由の getter にして、毎ストロークの再レンダリングを避ける
  // (= プレビュー rAF ループが関数経由で最新値を読むだけ。○リング / leadLine と同流儀)。
  // setStrokeActive は DrawingCanvas → CanvasStage 経由で呼ばれる stable callback。
  readonly setStrokeActive: (active: boolean) => void;
  readonly isStrokeActive: () => boolean;
  // --- スポイト持続モード (§9.6 方針転換 2026-06-12、Modern 専用操作) ---
  // Modern ツール帯の「スポイト」ボタンが立てる持続モード。ON のとき CanvasStage の
  // DrawingCanvas が素 pointerdown を色拾い経路 (`pickColorFromEngine` → onColorChange)
  // に流し、ストローク開始を抑止する。色拾い自体は protocol 非依存・client ローカル。
  // panActive と同じく engine/canvas は CanvasStage (共有 chrome を読む) が持つため、
  // spuit モードも共有 chrome に置くのが筋 (Modern だけがトグルし、Classic は呼ばない
  // ので常に false = 既存のジェスチャのみ挙動を維持)。
  readonly spuitMode: boolean;
  readonly setSpuitMode: (next: boolean) => void;
  // --- スポイト確定シグナル (最近色厳密化、2026-06-12、Modern 専用) ---
  // DrawingCanvas (CanvasStage 配下) がスポイト操作の確定 (= pointerup) で「この操作で拾った
  // 確定の1色」を通知する stable callback。chrome が登録ハンドラ (registerSpuitCommitHandler)
  // へ転送し、ModernLayout が `useRecentColors.recordColor` を登録する。これでドラッグ通過色を
  // 最近色から除外し、確定の1色だけを記録できる。`onColorChange` (毎サンプルの pen.color 反映)
  // とは独立。Classic はハンドラを登録しない (= no-op、最近色機能を持たないので無影響)。
  readonly onSpuitCommit: (rgb24: number) => void;
  readonly registerSpuitCommitHandler: (handler: ((rgb24: number) => void) | null) => void;
  // --- layers ---
  readonly layerVisibilityMap: Record<number, boolean>;
  readonly handleSelectActiveLayer: (layerId: number) => void;
  readonly handleToggleLayerVisible: (layerId: number, visible: boolean) => void;
  /** レイヤー表示濃度 (自分の画面だけ・永続しない)。index = layerId、ドラッグ中はライブ値。 */
  readonly layerOpacities: readonly number[];
  readonly layerOpacityControl: LayerOpacityControl | null;
  readonly resetLayerOpacities: () => void;
  /** レイヤーのサムネ (layer-thumbnails-2026-10-13.md)。パネルが canvas を登録する窓口 (null = 番号だけ)。 */
  readonly layerThumbnailHub: LayerThumbnailHub | null;
  /** 「レイヤーのサムネイル」のユーザー設定 (既定 ON、この端末のみ)。 */
  readonly layerThumbnailsEnabled: boolean;
  readonly onLayerThumbnailsEnabledChange: (next: boolean) => void;
  /** 遅い端末でエンジンが自動停止した (そのタブの間は戻らない)。 */
  readonly layerThumbnailsStopped: boolean;
  /**
   * 前回この部屋で非表示にしていたレイヤー (昇順)。**空でないときだけ**入室時に
   * 確認ダイアログを出す (feedback-triage-2026-08-12.md §1 B-2)。
   *
   * 黙って復元しないのは (a) 根本原因が「隠したことを忘れる」ことなので黙って戻すと
   * 同じ混乱が別の形で起きる (b) 共有キャンバスなので留守中に他の人がそのレイヤーへ
   * 描いている可能性があり、黙って復元すると**他人の作業を無断で隠す**ため。
   */
  readonly storedHiddenLayers: readonly number[];
  /** 「非表示のままにする」— 保存されていた非表示状態を engine に反映して確認を閉じる。 */
  readonly keepStoredLayerVisibility: () => void;
  /** 「すべて表示する」— 保存を破棄して全表示のまま確認を閉じる (既定側)。 */
  readonly showAllLayers: () => void;
  // canvas 背景の表示/非表示 (2026-06-20 canvas-background-color §5/B)。layer ではない
  // ので layerVisibilityMap と分離。👁 のみローカルトグル (broadcast しない)。
  readonly backgroundVisible: boolean;
  readonly handleToggleBackgroundVisible: (visible: boolean) => void;
  // canvas 背景 *色* (= 下地色 rgb24)。A-4 ■ swatch (canvas-background-color §4.2)。
  // engine に getBackground() が無いので React が権威 state を持つ。■ swatch の表示色 +
  // ColorWheelPalette の seed 値。remote の whiteboard.background-set broadcast / snapshot
  // meta を SessionProvider.onBackgroundChange seam 経由で取り込み (非オーナーの ■ も他者の
  // 変更を反映)、オーナー操作はローカルプレビューで即更新する。
  readonly backgroundRgb: number;
  // 背景色変更。オーナー (canSetBackground) の ColorWheelPalette onColorChange に繋ぐ。
  // (a) 即ローカルプレビュー (engine.setBackground + state)、(b) trailing debounce で
  // sendBackgroundSet 1 発 (§4.3 broadcast スパム / events.bin 肥大回避)。
  readonly handleBackgroundColorChange: (rgb: number) => void;
  // 背景色変更の perm (canvas-background-color §4.2)。⚠️ canClear と別物:
  // hasPerm(Perm.CLEAR) 単独 = room.clearOpen 非依存 (全消し開放部屋でも背景は owner 限定)。
  readonly canSetBackground: boolean;
  // --- name labels / zoom ---
  readonly nameLabelMode: boolean;
  readonly nameLabels: ReadonlyArray<NameLabel>;
  readonly zoom: number;
  /** キャンバス 1px の CSS 上の大きさ (ドットバイドット時は zoom / dpr)。CSS 幾何はこちら。 */
  readonly displayScale: number;
  // --- canvas 左右反転 (view flip, canvas-flip.md) ---
  // engine が single source of truth で flipX を保持し、zoom と同型で React 側に
  // ミラーする。DrawingCanvas (入力 un-flip) / overlay (X 再マップ) / ToolPanel
  // (トグル UI) / Classic の V キー listener が chrome 経由でこの値とトグルを読む。
  // canvas-flip.md 上 flip は Classic 専用挙動だが、engine/canvas/overlay は
  // CanvasStage (= 共有 chrome を読む) が永続描画するため、flip 配線も共有 chrome に
  // 置くのが筋 (= K/O 共有化と同じ流儀)。Modern は ToolPanel に flip トグルを露出
  // しないので実質 Classic でのみ操作される。
  readonly flipX: boolean;
  readonly handleToggleFlip: () => void;
  readonly handleToggleNameLabelMode: () => void;
  readonly handleZoomIn: () => void;
  readonly handleZoomOut: () => void;
  // Classic 固有: キーボード `0` で等倍リセット (ZOOM_MIN)。
  readonly handleZoomReset: () => void;
  // 2026-06-12 zoom-to-cursor (素のホイール=ズーム): CanvasStage の wheel listener から
  // 「direction (+1=in / -1=out) + コンテナ client box 内ポインタ位置 (px,py)」を渡して
  // 呼ぶ。カーソル直下の canvas 座標がカーソル位置に留まるよう zoom 後に scroll を補正する。
  readonly handleZoomAtPointer: (direction: 1 | -1, pointerX: number, pointerY: number) => void;
  // M1-a 分数ズーム (modern-phone-shell-2026-07-02.md §3.1): 連続倍率の絶対値指定 +
  // ポインタアンカー版。後続 M1-c のピンチズームが 2 本指中心を anchor に連続倍率で呼ぶ。
  // clamp は `[ZOOM_FIT_MIN, ZOOM_MAX]` (zoom.ts)。± ボタン / ホイールの CSP 方式
  // ステップ (決定2 §6) は従来どおり handleZoomIn/Out/AtPointer (zoomStepIn/Out) を使う。
  readonly handleZoomToAtPointer: (nextZoom: number, pointerX: number, pointerY: number) => void;
  // M1-a 入室時フィット: canvas 全景が表示スロットに収まる zoom を適用する (収まって
  // いれば等倍 1)。入室時 (スマホのみ自動適用) のほか、後続スライスのフィットボタン /
  // ピンチ下限からも呼べる公開 API。
  readonly applyFitZoom: () => void;
  // --- 2026-09-29 viewport transform (決定5、§6) ---
  // pan オフセット `{tx,ty}` (= 表示枠左上から canvas 左上までのオフセット) の
  // single source of truth。高頻度更新のため React state ではなく ref (= 値の読み
  // 取りは `.current`、変化の購読はできない — CanvasStage/Preview は rAF/イベント
  // 駆動で毎回 `.current` を読む設計)。
  readonly viewportRef: React.RefObject<ViewportOffset>;
  /**
   * `viewportRef` の値を DOM (canvas + overlay 群を包む wrapper) へ transform と
   * して適用する対象要素の ref。CanvasStage がこの div に `ref=` で attach する。
   */
  readonly viewportLayerRef: React.RefObject<HTMLDivElement | null>;
  /** `viewportRef` の唯一の書き込み経路 (ref 更新 + DOM transform 適用の両方を行う)。 */
  readonly setViewport: (next: ViewportOffset) => void;
  /**
   * 画面座標 ⇔ canvas 座標の変換窓口 (canvas-rotation 段取り 1、`viewport.ts` §1b)。
   * 入力 (`DrawingCanvas`) と全 overlay はこれ経由で座標を変換し、
   * `getBoundingClientRect` の比率計算や flip の `width - x` 補正を自前で持たない。
   * 参照は mount 中不変 (= 値は呼び出しごとに最新を読む getter 方式)。
   */
  readonly viewMapper: CanvasViewMapper;
  /**
   * 現在の zoom のまま、canvas が表示スロットより小さい軸を中央寄せする (決定5
   * 「入室時 (1x) は canvas が枠より小さい軸は中央寄せ」)。zoom も変える
   * mobile 入室時フィットは `applyFitZoom` を使う。CanvasStage が engine ready +
   * 表示スロット計測済みの初回 (desktop) に呼ぶ。
   */
  readonly centerViewportAtCurrentZoom: () => void;
  // --- 2026-10-03 キャンバス表示回転 (canvas-rotation 段取り 2、段取り 3 の UI が使う公開 API) ---
  // 回転は表示だけの transient 状態 (リロードで 0°、protocol / engine / snapshot 不触)。
  // 持ち主は tx/ty と同じくここ (理由: 回転は描画 / canvas 要素の CSS には効かず、wrapper の
  // `transform` に tx/ty と **同じ 1 回の書き込み** で当てる純粋な表示状態 — zoom/flipX のように
  // canvas 要素自体の transform を engine が持つ必要が無く、clamp / アンカー計算も tx/ty と
  // 一体で動く)。
  /** 現在の回転 (度、時計回り正、`[0, 360)`)。React ミラー (角度表示用)。 */
  readonly rotation: number;
  /** 最新の回転 (render を待たず ref から読む。ジェスチャ / ドラッグの開始角用)。 */
  readonly getRotation: () => number;
  /** 回転の変更通知を購読する (戻り値は解除関数)。 */
  readonly onRotationChange: (listener: (rotation: number) => void) => () => void;
  /**
   * 回転を `nextRotation` (度、絶対値) にする。回転軸は client 座標 `(clientX, clientY)` の
   * 真下にある canvas 点 (= 2 本指の重心)。ズーム・パンと同時に呼べる。
   */
  readonly rotateToAtClient: (nextRotation: number, clientX: number, clientY: number) => void;
  /** 回転を `nextRotation` にする (回転軸は表示枠の中央。ボタン・キー・PC ドラッグ用)。 */
  readonly rotateToAtSlotCenter: (nextRotation: number) => void;
  /** 左 (反時計回り) 回転 1 ステップ (`ROTATION_STEP_DEG`)。registry `view.rotateLeft`。 */
  readonly handleRotateLeft: () => void;
  /** 右 (時計回り) 回転 1 ステップ。registry `view.rotateRight`。 */
  readonly handleRotateRight: () => void;
  /** 回転を 0° に戻す (表示枠中央まわり)。registry `view.rotateReset` / 段取り 3 の角度表示タップ。 */
  readonly handleRotateReset: () => void;
  /**
   * 回転ツール (R) が選択中か。スポイトと同じ持続モード: 別ツール (ブラシ・消しゴム・スポイト等)
   * を選ぶまで続き、canvas ドラッグが回転になる。
   */
  readonly rotateToolActive: boolean;
  readonly setRotateToolActive: (next: boolean) => void;
  /** 回転ツールを選ぶ (registry `tool.rotate` / 段取り 3 のツールレールのボタン)。 */
  readonly onSelectRotateTool: () => void;
  // --- 範囲の移動・コピー (docs/_wip/region-move-2026-10-15.md) ---
  /**
   * 範囲の道具が選択中か。スポイト・回転ツールと同じ持続ツール: 別のツール (ペン・消しゴム・
   * スポイト・回転等) を選ぶと外れる。選択中は canvas のドラッグが範囲選択になる (DrawingCanvas)。
   */
  readonly regionToolActive: boolean;
  /** 範囲の道具を選ぶ (Modern ツール帯 / Classic「道具 ▸」/ スマホ「ブラシ設定」シート)。 */
  readonly onSelectRegionTool: () => void;
  /**
   * 範囲の入口ボタン (Modern 左ツール列 / Classic「道具 ▸」と小窓 / スマホのブラシ設定ボタンと
   * シート内) に「New!」バッジを出すか。範囲の道具を一度でも選ぶと false (localStorage 永続、
   * Classic / Modern 共有の単一 key)。
   */
  readonly showRegionNewBadge: boolean;
  /** 範囲の移動・コピーの状態と操作 (範囲・プルダウン・浮かせる・確定)。 */
  readonly region: RegionMoveController;
  // --- engine + imagepaste ---
  readonly engine: CanvasDrawingEngine | null;
  readonly canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  readonly onEngineReady: (next: CanvasDrawingEngine | null) => void;
  readonly imagepaste: ReturnType<typeof useImagepaste>;
  // --- permissions / actions ---
  readonly canImagepaste: boolean;
  readonly canClear: boolean;
  readonly handleClear: () => void;
  readonly handleCapture: () => void;
  /**
   * 荒らし復旧 UI 第 1 段階 (vandalism-recovery-ui-2026-09-27.md §2)。現在表示中
   * キャンバスの全層合成 Blob を返す (`handleCapture` と同じ export 経路、DL は
   * トリガしない)。「時点を選んで戻す」の比較画面が「今の絵」プレビューに使う。
   * 失敗時は null (呼び出し側がプレースホルダ表示に倒す)。
   */
  readonly getCurrentCanvasBlob: () => Promise<Blob | null>;
}

/**
 * 描画 chrome の共通 state + ハンドラを構築する。ロジックの根拠コメントは
 * ClassicLayout.tsx 側 (= 原典) を参照のこと。本 hook はそれを共有化した複製。
 */
export const useRoomChrome = (
  state: RoomState,
  setClearConfirmOpen: (open: boolean) => void,
  // §9.7 Phase 2: canvas overflow scroll の anchor (= RoomLayout 所有、CanvasStage と共有)。
  // imagepaste preview の初期位置を「現在の表示範囲の中央」に置くため、chrome が
  // getViewportRect を組み立てるのに使う (= Classic 原典の挙動を共有コントローラで再現)。
  // 未指定なら canvas 全体中央 (= hook 既定) に倒す。
  canvasScrollRef?: React.RefObject<HTMLDivElement | null>,
  // アプリ起動時に判定した入室歴 (aa-default-and-trouble-tips-2026-10-27.md §1)。'first' の端末だけ
  // 最初の入室で AA の明示設定 on を保存する。未指定 (テスト等) は何もしない。
  visitor?: VisitorKind,
): RoomChrome => {
  const {
    transport,
    sendImagepasteRequest,
    sendImagepasteConfirm,
    pushSystemLine,
    registerEngine,
    sendBackgroundSet,
    onBackgroundChange,
  } = useSession();
  // 再接続中は描けない (docs/_wip/reconnect-draw-lock-2026-10-12.md): サーバへ送る操作の門。
  const connectionLocked = useConnectionLocked();

  // W3 Classic レイヤー: デフォルトアクティブ層は最前面 (3 層なら id2)。
  const [pen, setPen] = useState<PenConfig>(() => ({
    ...DEFAULT_PEN_CONFIG,
    layerId: defaultActiveLayerId(),
  }));

  // 消ゴム復帰用に直前のペン種 (= 最後に使った非消しゴムツール、設計書の `lastPen`) を保持。
  // 初期値 = 既定ペン (DEFAULT_PEN_CONFIG.pen = 'brush')。これにより設計書 §2.1 の
  // 「直前ペンが無ければ既定ペン (= normal)」は seed 値で自然に満たされる (= 常に非 null)。
  const [lastNonEraserPen, setLastNonEraserPen] = useState<StrokePen>(DEFAULT_PEN_CONFIG.pen);

  // M-bucket (CSP マーカー bucket 流儀、ClassicLayout 原典踏襲): flat ⇄ marker の
  // bucket toggle。bucket 外 (brush/eraser) からは直前選択 (lastMarkerKind) を選ぶ。
  // 初期値 'marker' (= 80px サイドバーで先頭表示される側、UI 初回操作との整合)。
  const [lastMarkerKind, setLastMarkerKind] = useState<'flat' | 'marker'>('marker');
  const lastMarkerKindRef = useRef<'flat' | 'marker'>('marker');
  lastMarkerKindRef.current = lastMarkerKind;

  // 太さ 6 cell の現在値 (描画 / 消しゴム バケット別)。
  const [drawPenWidths, setDrawPenWidths] = useState<ReadonlyArray<number>>(DEFAULT_PEN_WIDTHS);
  const [drawSelectedPenWidthIndex, setDrawSelectedPenWidthIndex] = useState<number>(0);
  const [eraserPenWidths, setEraserPenWidths] =
    useState<ReadonlyArray<number>>(DEFAULT_ERASER_PEN_WIDTHS);
  const [eraserSelectedPenWidthIndex, setEraserSelectedPenWidthIndex] = useState<number>(
    DEFAULT_ERASER_PEN_WIDTHS.length - 1,
  );

  // 消しゴム独立サイズ (2 バケット方式)。color / alpha は global 共有。
  const [widthBuckets, setWidthBuckets] = useState<WidthBuckets>({
    drawWidth: DEFAULT_PEN_CONFIG.width,
    eraserWidth: 60,
  });
  const effectiveWidth = resolveActiveWidth(
    pen.pen,
    widthBuckets.drawWidth,
    widthBuckets.eraserWidth,
  );
  // pen.width をアクティブバケットの実効値に同期 (下流を無改修に保つ)。
  useEffect(() => {
    setPen((p) => (p.width === effectiveWidth ? p : { ...p, width: effectiveWidth }));
  }, [effectiveWidth]);

  // M-bucket sync (ClassicLayout 原典踏襲): pen.pen が flat / marker に切替わったら
  // lastMarkerKind を追従。bucket 外 (brush/eraser) では触らない (= 直前 bucket 選択を保持)。
  useEffect(() => {
    if (pen.pen === 'flat' || pen.pen === 'marker') {
      setLastMarkerKind(pen.pen);
    }
  }, [pen.pen]);

  // lastPen 追跡 (spuit-sample-modes-2026-06-22 §2.1、consult 2026-06-27): アクティブ
  // ツールが **非消しゴム** に変わるたびに lastNonEraserPen を更新し、消しゴム選択時は
  // 上書きしない。全ツール選択経路 (onSelectPen / onSelectBrush / onMarkerBucketToggle /
  // onToggleEraser の復帰 / モードB スポイト色pick 復帰) が pen.pen を通るので、ここ 1 箇所
  // に集約するのが自然 (= 各ハンドラに散らさない、別 state を持ち込まない)。これがモードB
  // 対称トグルの「直前のペンへ復帰」先になる。
  useEffect(() => {
    if (pen.pen !== 'eraser') setLastNonEraserPen(pen.pen);
  }, [pen.pen]);

  // タブ最後使用ペン (CSP流 循環ショートカット、shortcut-settings.md「CSP の実モデル」
  // 2026-08-21 確定「次のタブへ進んだときに選ばれるペンは、そのタブで最後に使った
  // ペン」)。上の lastNonEraserPen と同じ「pen.pen の変化を追跡する」流儀を、タブ単位に
  // 拡張したもの。pen がどのタブにも属さない (= 消しゴム等、`tabForPen` が null) ときは
  // 触らない。初期値は各タブの先頭ペン (`DEFAULT_PEN_FOR_TAB`) — そのタブを一度も
  // 使っていないセッションでタブ action を叩いても、タブの代表ペンへ落ちる。
  const [lastUsedPenForTab, setLastUsedPenForTab] =
    useState<Readonly<Record<PenTabId, StrokePen>>>(DEFAULT_PEN_FOR_TAB);
  const lastUsedPenForTabRef = useRef(lastUsedPenForTab);
  lastUsedPenForTabRef.current = lastUsedPenForTab;
  useEffect(() => {
    const tab = tabForPen(pen.pen);
    if (tab === null) return;
    setLastUsedPenForTab((m) => (m[tab] === pen.pen ? m : { ...m, [tab]: pen.pen }));
  }, [pen.pen]);

  // 循環ショートカットの「記憶した前回位置」(RoomChrome.shortcutCyclePositions の doc
  // 参照)。この hook 自体の寿命 (= 入室 mount・退室 unmount) で「退室・入室時のみ
  // リセット」を満たす。
  const [shortcutCyclePositions, setShortcutCyclePositions] = useState<Record<string, number>>({});
  const onShortcutCycleIndexChange = useCallback((binding: string, index: number): void => {
    setShortcutCyclePositions((m) => (m[binding] === index ? m : { ...m, [binding]: index }));
  }, []);

  const eraserActive = isEraserActive(pen.pen);
  const activePenWidths = eraserActive ? eraserPenWidths : drawPenWidths;
  const activeSelectedPenWidthIndex = eraserActive
    ? eraserSelectedPenWidthIndex
    : drawSelectedPenWidthIndex;

  // MASK 2 slot + UI type。
  const [slot1, setSlot1] = useState<MaskSlotValue>(null);
  const [slot2, setSlot2] = useState<MaskSlotValue>(null);
  const [maskTypeUI, setMaskTypeUI] = useState<MaskTypeUI>('normal');

  // ペン専用モード (Palm Rejection)。session-local。
  const [penOnlyMode, setPenOnlyMode] = useState<boolean>(false);
  const handlePenAutoEnable = useCallback((): void => {
    setPenOnlyMode((prev) => (prev ? prev : true));
  }, []);

  // 手のひら (pan) モードの有効状態 (§9.7 Phase 2)。CanvasStage が読んでドラッグ pan を行う。
  const [panActive, setPanActive] = useState<boolean>(false);

  // ストローク進行中シグナル (プレビュー負荷抑制、feature/preview-perf 2026-06-13)。
  // DrawingCanvas が pointerdown→pointerup の間 true をセットする。プレビューは ref 経由
  // の getter でこれを読む (= 再レンダリング不要、毎ストロークで React tree を汚さない)。
  const strokeActiveRef = useRef<boolean>(false);
  const setStrokeActive = useCallback((active: boolean): void => {
    strokeActiveRef.current = active;
  }, []);
  const isStrokeActive = useCallback((): boolean => strokeActiveRef.current, []);

  // スポイト持続モードの有効状態 (§9.6、Modern 専用)。CanvasStage の DrawingCanvas が
  // 読んで素クリック色拾い・ストローク非開始を切り替える。Classic は set しない (= false)。
  const [spuitMode, setSpuitModeRaw] = useState<boolean>(false);
  // 回転ツール (R、canvas-rotation 段取り 2): スポイトと同じ持続ツール。描画ツール (ブラシ・
  // 消しゴム・各ペン) / スポイトを選ぶと外れる (= 下の各 onSelect* と setSpuitMode から
  // `setRotateToolActive(false)`)。回転ツール中は canvas ドラッグが回転 (CanvasStage)。
  const [rotateToolActive, setRotateToolActiveRaw] = useState<boolean>(false);
  // 範囲の道具 (範囲の移動・コピー)。回転ツール・スポイトと同じ持続ツールで、相互排他。
  const [regionToolActive, setRegionToolActive] = useState<boolean>(false);
  // 回転ツールを外す = 「別のツールへ移る」操作 (各 onSelect* が呼ぶ) なので、範囲の道具も
  // 同じ口で外す。回転ツールへ入る (`true`) ときも範囲の道具から抜ける。範囲の道具へ入る
  // 経路 (`onSelectRegionTool`) だけは Raw を直接触る。
  const setRotateToolActive = useCallback((next: boolean): void => {
    setRotateToolActiveRaw(next);
    setRegionToolActive(false);
  }, []);
  // Modern の全ツール選択経路は「描画ツールへ抜ける」ときに `setSpuitMode(false)`、スポイトへ
  // 入るときに `setSpuitMode(true)` を呼ぶ。どちらも回転ツール・範囲の道具からは抜ける操作なので、
  // ここ 1 箇所で両方を解除する (= ModernLayout の呼び出し群を触らずに相互排他)。
  const setSpuitMode = useCallback((next: boolean): void => {
    setSpuitModeRaw(next);
    setRotateToolActiveRaw(false);
    setRegionToolActive(false);
  }, []);
  const onSelectRotateTool = useCallback((): void => {
    setSpuitModeRaw(false);
    setRotateToolActiveRaw(true);
    setRegionToolActive(false);
  }, []);
  // 範囲の New! バッジ (どの入口・どのレイアウトから選んでも onSelectRegionTool に合流する)。
  const regionNewBadgeSeenStoreRef = useRef(createRegionNewBadgeSeenStore());
  const [regionNewBadgeSeen, setRegionNewBadgeSeen] = useState<boolean>(() =>
    regionNewBadgeSeenStoreRef.current.hasSeen(),
  );
  const onSelectRegionTool = useCallback((): void => {
    setSpuitModeRaw(false);
    setRotateToolActiveRaw(false);
    setRegionToolActive(true);
    regionNewBadgeSeenStoreRef.current.markSeen();
    setRegionNewBadgeSeen(true);
  }, []);

  // スポイト確定シグナル (最近色厳密化、2026-06-12): DrawingCanvas → ここ → Modern の
  // recordColor。ref に登録ハンドラを保持し、stable な onSpuitCommit が転送する (= DrawingCanvas
  // へ渡す callback の identity を固定して effect 再 attach を避ける)。Classic は未登録 = no-op。
  const spuitCommitHandlerRef = useRef<((rgb24: number) => void) | null>(null);
  const registerSpuitCommitHandler = useCallback(
    (handler: ((rgb24: number) => void) | null): void => {
      spuitCommitHandlerRef.current = handler;
    },
    [],
  );
  const onSpuitCommit = useCallback((rgb24: number): void => {
    spuitCommitHandlerRef.current?.(rgb24);
  }, []);

  // 筆圧 mapping 設定 (ペン種ごと別キーで localStorage 永続)。
  const pressureStore = useMemo(() => createPenPressureStore(), []);
  const [pressureMap, setPressureMap] = useState<Record<StrokePen, PenPressureSettings>>(() => ({
    brush: pressureStore.read('brush'),
    flat: pressureStore.read('flat'),
    marker: pressureStore.read('marker'),
    eraser: pressureStore.read('eraser'),
    // texture spike: brush 相当の筆圧設定で埋める (texture-brush-design.md §8)。
    texture: pressureStore.read('texture'),
    // スクリーントーン (texture-brush-design.md §12.2): 筆圧グラデ不採用だが
    // `Record<StrokePen, ...>` 充足のため読み込む。
    tone: pressureStore.read('tone'),
    // エアブラシ (airbrush-design-2026-08-15.md §2.5): brush/texture 相当の 2 軸筆圧。
    airbrush: pressureStore.read('airbrush'),
    // クレヨン / パステル / ドット (phase11-light-pens-2026-08-21.md): まだ UI に
    // 露出していないが `Record<StrokePen, ...>` 充足のため読み込む。
    crayon: pressureStore.read('crayon'),
    pastel: pressureStore.read('pastel'),
    dot: pressureStore.read('dot'),
  }));
  const onPressureChange = useCallback(
    (next: PenPressureSettings): void => {
      const kind = pen.pen;
      pressureStore.write(kind, next);
      setPressureMap((prev) => ({ ...prev, [kind]: next }));
    },
    [pen.pen, pressureStore],
  );
  const currentPressure = pressureMap[pen.pen];

  // 手ぶれ補正レベル (§9.8)。初期値はストアから読む (per-user localStorage、default off)。
  // engine への反映は「engine が ready になったタイミング」+「onStabilizerChange」で行う
  // (下記)。engine は §9.7 で共有・永続なので 1 インスタンスに対し setStabilizer する。
  const stabilizerStore = useMemo(() => createStabilizerPreferenceStore(), []);
  const [stabilizerLevel, setStabilizerLevel] = useState<StabilizerLevel>(() =>
    stabilizerStore.get(),
  );

  // engine + canvas ref。SessionProvider が engine を所有 (registerEngine)、
  // useImagepaste にも engine が必要なので React state でも持つ。
  const [engine, setEngine] = useState<CanvasDrawingEngine | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // 永続値を engine へ反映: engine が ready になった時 (= onEngineReady で engine が
  // セットされた時) に現在の stabilizerLevel を engine.setStabilizer で適用する。
  // engine が null の間はスキップし、ready 時に同期する (= mount 時の永続値反映)。
  // setStabilizer は次 stroke から反映される (engine 側で deferred、§9.8)。
  useEffect(() => {
    if (engine === null) return;
    engine.setStabilizer(stabilizerLevel);
  }, [engine, stabilizerLevel]);

  // 手ぶれ補正レベル変更: state 更新 + ストア永続 + engine へ反映。
  const onStabilizerChange = useCallback(
    (level: StabilizerLevel): void => {
      setStabilizerLevel(level);
      stabilizerStore.set(level);
      // engine が ready なら即反映 (null なら上の effect が ready 時に同期する)。
      engine?.setStabilizer(level);
    },
    [engine, stabilizerStore],
  );

  // ドットバイドット表示の設定 (端末ごと)。engine の初期値は DrawingCanvas が同じ store から
  // 読むので、ここは engine ready / 変更時に setDotByDotMode で揃える。
  const dotByDotStore = useMemo(() => createDotByDotPreferenceStore(), []);
  const [dotByDotMode, setDotByDotModeState] = useState<DotByDotMode>(() => dotByDotStore.get());
  useEffect(() => {
    if (engine === null) return;
    engine.setDotByDotMode(dotByDotMode);
  }, [engine, dotByDotMode]);
  const onDotByDotModeChange = useCallback(
    (next: DotByDotMode): void => {
      setDotByDotModeState(next);
      dotByDotStore.set(next);
    },
    [dotByDotStore],
  );
  // 対象の画面か (dpr が 1 超 2 未満)。dpr の変化は displayScale 変化と一緒に再評価される。
  const dotByDotActive = engine !== null && isDotByDotDpr(engine.getDevicePixelRatio());

  // アンチエイリアス (§9.9 + 表現パリティ §1.1、consult 2026-06-13): tri-state
  // (未設定 | on | off)。明示設定 (= localStorage に保存値あり) はレイアウト間で共有、
  // 未設定は **共通既定 off** (consult 2026-06-13 続報、旧レイアウト別既定 Modern=on は
  // 撤廃 = レイアウト切替で線の出力が変わる矛盾を排除) に解決する。実効値を pen.aa へ
  // 同期し、wire への反映は DrawingCanvas (sendStrokeStart) が pen.aa を読む。
  // 未設定なら両レイアウトとも aa=false = 旧 Java 互換の描き味 bit-perfect 不変。
  const aaStore = useMemo(() => createAaPreferenceStore(), []);
  // 新しい端末だけ既定「あり」: 初回 render 前に seed する (idempotent = StrictMode 二重実行・remount 可)。
  // 端末の既定は別キー (自分で選んだ値と区別するため)。解決順: 自分で選んだ値 → 端末の既定 → off。
  const [aaDeviceDefault] = useState<boolean | null>(() => {
    if (visitor !== undefined) seedAaForFirstVisitor(visitor, aaStore);
    return aaStore.getDeviceDefault();
  });
  const [aaExplicit, setAaExplicit] = useState<boolean | null>(() => aaStore.getExplicit());
  const aaEnabled = resolveEffectiveAa(aaExplicit, aaDeviceDefault);
  // 実効値を pen.aa へ同期 (mount / 明示変更のすべてで効く)。
  useEffect(() => {
    setPen((p) => (p.aa === aaEnabled ? p : { ...p, aa: aaEnabled }));
  }, [aaEnabled]);
  // AA 明示設定の単一経路 (ModernToolProperties トグル + PenSettingsModal「ペン詳細」)。
  const onAaChange = useCallback(
    (next: boolean): void => {
      setAaExplicit(next);
      aaStore.set(next);
    },
    [aaStore],
  );

  // ブラシカーソル種別 (consult 2026-06-21): per-user localStorage、既定ブラシ円。
  // engine には一切触れない (= 純粋に UI 見た目の設定)。CanvasStage が innerWrapper の
  // data 属性 + BrushCursorOverlay の cursorType prop へ流す (= レイアウト非依存に効く)。
  const brushCursorStore = useMemo(() => createBrushCursorPreferenceStore(), []);
  const [brushCursorType, setBrushCursorType] = useState<BrushCursorType>(() =>
    brushCursorStore.get(),
  );
  const onBrushCursorTypeChange = useCallback(
    (next: BrushCursorType): void => {
      setBrushCursorType(next);
      brushCursorStore.set(next);
    },
    [brushCursorStore],
  );

  // 自分のカスタムカーソルを自分の画面にも表示するか (作者 staging フィードバック
  // 2026-09-19 follow-up): per-user localStorage、既定 ON。engine には一切触れない
  // (= BrushCursorOverlay の描画有無を切り替えるだけの純粋な UI 設定)。
  const showOwnCustomCursorStore = useMemo(() => createShowOwnCustomCursorPreferenceStore(), []);
  const [showOwnCustomCursor, setShowOwnCustomCursor] = useState<boolean>(() =>
    showOwnCustomCursorStore.get(),
  );
  const onShowOwnCustomCursorChange = useCallback(
    (next: boolean): void => {
      setShowOwnCustomCursor(next);
      showOwnCustomCursorStore.set(next);
    },
    [showOwnCustomCursorStore],
  );

  // スポイトのサンプル元モード (spuit-sample-modes-2026-06-22): per-user localStorage、
  // 既定 A 'display'。engine には触れず、DrawingCanvas が pick 時に samplePixel へ渡す
  // (= 値を持つだけ、適用は CanvasStage 配下の DrawingCanvas)。レイアウト非依存。
  const spuitSampleStore = useMemo(() => createSpuitSamplePreferenceStore(), []);
  const [spuitSampleMode, setSpuitSampleMode] = useState<SpuitSampleMode>(() =>
    spuitSampleStore.get(),
  );
  const onSpuitSampleModeChange = useCallback(
    (next: SpuitSampleMode): void => {
      setSpuitSampleMode(next);
      spuitSampleStore.set(next);
    },
    [spuitSampleStore],
  );

  // NameLabel / zoom / flipX を engine から React にミラー。
  const [nameLabelMode, setNameLabelMode] = useState<boolean>(true);
  const [nameLabels, setNameLabels] = useState<ReadonlyArray<NameLabel>>([]);
  const [zoom, setZoom] = useState<number>(ZOOM_MIN);
  // ドットバイドット表示 (2026-10-28): キャンバス 1px が CSS で何 px か。`zoom` は利用者の
  // 倍率 (ラベル / ステップ / フィット)、CSS の幾何 (wrapper 寸法・overlay 配置・座標変換)
  // はこちら。dpr (1,2) の自動時だけ zoom と異なる (= zoom / dpr)。
  const [displayScale, setDisplayScale] = useState<number>(ZOOM_MIN);
  // 2026-06-07 キャンバス左右反転 (view flip, canvas-flip.md §3): engine が flipX を
  // 所有する single source of truth。zoom と同型で React 側にミラー (初期 sync +
  // listener subscribe + engine 差し替え時 false リセット)。
  const [flipX, setFlipX] = useState<boolean>(false);

  // 2026-09-29 viewport transform 移行 (決定5、§6): pan オフセット `{tx,ty}` の
  // single source of truth (`infrastructure/drawing/viewport.ts`)。高頻度更新
  // (ドラッグ pan / ピンチ / ホイール) で React 再レンダリングを起こさないよう
  // ref に持ち、DOM 適用 (`viewportLayerRef` = CanvasStage の canvas+overlay 群
  // wrapper) は `applyViewportTransform` の直接書き込みで行う (旧
  // `wrapper.scrollLeft` 直書きと同じ perf 特性)。`setViewport` がこの唯一の
  // 書き込み経路 — CanvasStage (pan ドラッグ / ジェスチャ) とこのファイルの
  // zoom/flip ハンドラの両方がこれを通る。
  const viewportRef = useRef<ViewportOffset>(ORIGIN_VIEWPORT);
  const viewportLayerRef = useRef<HTMLDivElement | null>(null);
  // 表示回転 (canvas-rotation 段取り 2)。tx/ty と同じ理由で ref が source of truth
  // (高頻度更新 = ひねり / ドラッグで React 再レンダリングを挟まない)。角度表示用に
  // React state へミラーし、変更通知 listener も持つ (段取り 3 の UI が購読する)。
  const rotationRef = useRef<number>(0);
  const [rotation, setRotationState] = useState<number>(0);
  const rotationListenersRef = useRef<Set<(rotation: number) => void>>(new Set());
  // 回転の中心 (表示 box 中央 = `W*z/2, H*z/2`)。engine の live zoom から求める
  // (wrapper の CSS box は React 再描画まで古い zoom のサイズなので使わない)。
  // 参照する ref はこの下で宣言されるが、呼ばれるのは render 後なので問題ない。
  const rotationCenter = (): { cx: number; cy: number } => {
    const z = engineLatestRef.current?.getDisplayScale() ?? displayScaleMirrorRef.current;
    const canvas = canvasRef.current;
    const w = canvas?.width || canvasSizeMirrorRef.current.width;
    const h = canvas?.height || canvasSizeMirrorRef.current.height;
    return { cx: (w * z) / 2, cy: (h * z) / 2 };
  };
  const rotationCenterRef = useRef(rotationCenter);
  rotationCenterRef.current = rotationCenter;
  // 表示 (pan / zoom / 回転) の変更通知 (`viewMapper.onViewChange`)。zoom の変更も必ず
  // この後の setViewport を通るので、ここで通知すれば全部拾える。
  const viewListenersRef = useRef<Set<() => void>>(new Set());
  const notifyViewChange = (): void => {
    for (const l of viewListenersRef.current) l();
  };
  const notifyViewChangeRef = useRef(notifyViewChange);
  // ドットバイドット (割り切れない dpr) のとき、wrapper の画面位置を device pixel の
  // 境目に丸めた offset を返す (回転中は無加工)。DOM 書き込みとポインタ座標変換
  // (getTransform) の両方がこれを通るので、描いた位置と見た目がずれない。
  const snappedOffsetRef = useRef<(v: ViewportOffset, rotation: number) => ViewportOffset>(
    (v) => v,
  );
  const setViewport = useCallback((next: ViewportOffset): void => {
    viewportRef.current = next;
    const snapped = snappedOffsetRef.current(next, rotationRef.current);
    applyViewportTransform(
      viewportLayerRef.current,
      snapped.tx,
      snapped.ty,
      rotationRef.current,
      rotationCenterRef.current(),
    );
    notifyViewChangeRef.current();
  }, []);
  /** tx/ty と回転を 1 回の DOM 書き込みで同時に確定する (回転の唯一の書き込み経路)。 */
  const setViewportAndRotation = useCallback((next: ViewportOffset, nextRotation: number): void => {
    const r = normalizeRotation(nextRotation);
    const changed = r !== rotationRef.current;
    rotationRef.current = r;
    viewportRef.current = next;
    const snapped = snappedOffsetRef.current(next, r);
    applyViewportTransform(
      viewportLayerRef.current,
      snapped.tx,
      snapped.ty,
      r,
      rotationCenterRef.current(),
    );
    notifyViewChangeRef.current();
    if (!changed) return;
    setRotationState(r);
    for (const l of rotationListenersRef.current) l(r);
  }, []);
  const getRotation = useCallback((): number => rotationRef.current, []);
  const onRotationChange = useCallback((listener: (rotation: number) => void): (() => void) => {
    rotationListenersRef.current.add(listener);
    return () => {
      rotationListenersRef.current.delete(listener);
    };
  }, []);

  // 画面 ⇔ canvas 座標の変換窓口 (`viewport.ts` §1b)。getter が読む値 (engine の
  // zoom/flip、canvas 寸法、表示枠要素) は render を待たず常に最新を読みたい
  // (pointermove / rAF から呼ばれる) ので、React state ではなく ref 経由。
  // zoom / flip の source of truth は engine (= 実際の canvas 要素の CSS transform と
  // 一致する値)。engine 未接続の間だけ React ミラー値に倒す。
  const engineLatestRef = useRef<CanvasDrawingEngine | null>(null);
  engineLatestRef.current = engine;
  const displayScaleMirrorRef = useRef<number>(displayScale);
  displayScaleMirrorRef.current = displayScale;
  const flipMirrorRef = useRef<boolean>(flipX);
  flipMirrorRef.current = flipX;
  const canvasSizeMirrorRef = useRef<{ width: number; height: number }>(state.canvas);
  canvasSizeMirrorRef.current = state.canvas;
  snappedOffsetRef.current = (v, rotation) => {
    const eng = engineLatestRef.current;
    const slot = canvasScrollRef?.current;
    if (eng === null || !slot || rotation !== 0 || eng.getDotByDotDivisor() === 1) return v;
    return snapOffsetToDevicePixel(v, measureSlotOrigin(slot), eng.getDevicePixelRatio());
  };
  const viewMapper = useMemo<CanvasViewMapper>(
    () =>
      createViewMapper({
        getTransform: () => {
          const v = snappedOffsetRef.current(viewportRef.current, rotationRef.current);
          const eng = engineLatestRef.current;
          const canvas = canvasRef.current;
          return {
            tx: v.tx,
            ty: v.ty,
            zoom: eng?.getDisplayScale() ?? displayScaleMirrorRef.current,
            rotation: rotationRef.current,
            flipX: eng?.getFlipX() ?? flipMirrorRef.current,
            canvasWidth: canvas?.width || canvasSizeMirrorRef.current.width,
            canvasHeight: canvas?.height || canvasSizeMirrorRef.current.height,
          };
        },
        getSlotOrigin: () => {
          const slot = canvasScrollRef?.current;
          return slot ? measureSlotOrigin(slot) : null;
        },
        subscribe: (listener) => {
          viewListenersRef.current.add(listener);
          return () => {
            viewListenersRef.current.delete(listener);
          };
        },
      }),
    [canvasScrollRef],
  );

  useEffect(() => {
    if (engine === null) {
      setNameLabelMode(false);
      setNameLabels([]);
      setZoom(ZOOM_MIN);
      setDisplayScale(ZOOM_MIN);
      setFlipX(false);
      setViewportAndRotation(ORIGIN_VIEWPORT, 0);
      return;
    }
    setNameLabelMode(engine.getNameLabelMode());
    setNameLabels(engine.getNameLabels());
    setZoom(engine.getZoom());
    setDisplayScale(engine.getDisplayScale());
    setFlipX(engine.getFlipX());
    const unsubLabels = engine.onNameLabelsChange((s) => {
      setNameLabelMode(s.mode);
      setNameLabels(s.labels);
    });
    const unsubZoom = engine.onZoomChange((z) => setZoom(z));
    // 表示倍率の変化 (zoom / dpr / 設定)。zoom 起因のときは各ハンドラが viewport を計算
    // するので、ここでは「zoom が同じまま倍率だけ変わった」(= dpr 変化・設定切替) ときに
    // 枠中央の canvas 点を保って tx/ty を取り直す。
    let lastZoom = engine.getZoom();
    let lastScale = engine.getDisplayScale();
    const unsubScale = engine.onDisplayScaleChange((scale) => {
      setDisplayScale(scale);
      const z = engine.getZoom();
      const prevScale = lastScale;
      const sameZoom = z === lastZoom;
      lastZoom = z;
      lastScale = scale;
      if (!sameZoom || prevScale === scale) return;
      const wrapper = canvasScrollRef?.current;
      if (!wrapper) return;
      const slot = measureSlot(wrapper);
      const before = { ...viewMapper.getTransform(), zoom: prevScale };
      setViewport(
        viewportAfterZoomRotated(
          before,
          scale,
          slot.slotWidth / 2,
          slot.slotHeight / 2,
          slot.slotWidth,
          slot.slotHeight,
        ),
      );
    });
    const unsubFlip = engine.onFlipXChange((f) => setFlipX(f));
    return () => {
      unsubLabels();
      unsubZoom();
      unsubScale();
      unsubFlip();
    };
  }, [engine, setViewportAndRotation, setViewport, canvasScrollRef, viewMapper]);

  // ドットバイドット (割り切れない dpr) の再丸め: ウィンドウのリサイズで枠の原点が動くと、
  // 次の viewport 更新まで device pixel 境界への丸めが古くなってぼやける。枠の寸法変化と
  // window resize のたびに今の viewport を打ち直して丸め直す (有効なときだけ)。
  // biome-ignore lint/correctness/useExhaustiveDependencies: displayScale は張り直しの trigger
  useEffect(() => {
    if (engine === null || canvasScrollRef === undefined) return;
    if (engine.getDotByDotDivisor() === 1) return;
    const slot = canvasScrollRef.current;
    if (!slot) return;
    const resnap = (): void => setViewport({ ...viewportRef.current });
    window.addEventListener('resize', resnap);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(resnap);
      ro.observe(slot);
    }
    return () => {
      window.removeEventListener('resize', resnap);
      ro?.disconnect();
    };
    // displayScale: 自動の有効/無効 (mode / dpr 変化) が変わったら張り直すための trigger.
  }, [engine, canvasScrollRef, setViewport, displayScale]);

  const handleToggleNameLabelMode = useCallback((): void => {
    if (engine === null) return;
    engine.setNameLabelMode(!engine.getNameLabelMode());
  }, [engine]);

  // 2026-06-07 キャンバス左右反転: 名表示 toggle と同型。engine が単一 source-of-truth
  // なので setFlipX を直接呼ばず engine.setFlipX 経由 (onFlipXChange listener で React
  // state が同期する)。反転で画面中央に見えている canvas 座標が変わらないよう、
  // `viewportAfterFlipToggle` (旧 `scrollForFlipMirror` 相当) で tx を同期的に
  // 補正する (= 旧方式の `pendingFlipScrollRef` + `useLayoutEffect` は不要になった、
  // tx/ty はレイアウトに依存しないため zoom 変更と同じく同期計算できる)。
  const handleToggleFlip = useCallback((): void => {
    if (engine === null) return;
    const wasFlipX = engine.getFlipX();
    const nextFlipX = !wasFlipX;
    // 反転前の変換 (回転対応: 枠中央の canvas 点を保つ)。engine.setFlipX の前に取る。
    const before: ViewTransform = {
      ...viewMapper.getTransform(),
      flipX: wasFlipX,
      canvasWidth: state.canvas.width,
      canvasHeight: state.canvas.height,
    };
    engine.setFlipX(nextFlipX);
    const wrapper = canvasScrollRef?.current;
    if (!wrapper) return;
    const slot = measureSlot(wrapper);
    setViewport(viewportAfterFlipToggleRotated(before, nextFlipX, slot.slotWidth, slot.slotHeight));
  }, [engine, canvasScrollRef, state.canvas.width, state.canvas.height, setViewport, viewMapper]);

  // 決定2 (consult 2026-09-29、§6): ± ボタン / キーボード +/− は CSP 方式
  // (現在値 ×/÷ ZOOM_STEP_RATIO、ZOOM_ANCHORS をまたいだらアンカーで停止、
  // zoom.ts) — 1x 以上/未満を区別しない。旧「整数ステップのまま非退行」は
  // 仕様変更で撤廃。決定5 (§6): アンカーは枠中央 (= `viewportAfterZoom`)。
  // 旧方式 (α-feedback K) の `captureZoomCenter` + 事後 `useLayoutEffect` 補正は
  // 撤去した — tx/ty はレイアウト (要素サイズ) に依存しないので、zoom 変更と
  // 同じ関数呼び出し内で同期的に新しい tx/ty を計算・適用できる (DOM の resize を
  // 待つ必要が無い)。
  const zoomAtSlotCenter = useCallback(
    (nextZoom: number): void => {
      if (engine === null) return;
      const z = engine.getZoom();
      if (nextZoom === z) return;
      const wrapper = canvasScrollRef?.current;
      // 旧 zoom の変換 (engine.setZoom の前に取る)。
      const before = { ...viewMapper.getTransform(), zoom: engine.displayScaleOf(z) };
      engine.setZoom(nextZoom);
      if (!wrapper) return;
      const slot = measureSlot(wrapper);
      setViewport(
        viewportAfterZoomRotated(
          before,
          engine.getDisplayScale(),
          slot.slotWidth / 2,
          slot.slotHeight / 2,
          slot.slotWidth,
          slot.slotHeight,
        ),
      );
    },
    [engine, canvasScrollRef, setViewport, viewMapper],
  );
  const handleZoomIn = useCallback((): void => {
    if (engine === null) return;
    zoomAtSlotCenter(zoomStepIn(engine.getZoom()));
  }, [engine, zoomAtSlotCenter]);
  const handleZoomOut = useCallback((): void => {
    if (engine === null) return;
    zoomAtSlotCenter(zoomStepOut(engine.getZoom()));
  }, [engine, zoomAtSlotCenter]);
  const handleZoomReset = useCallback((): void => {
    zoomAtSlotCenter(ZOOM_MIN);
  }, [zoomAtSlotCenter]);

  // 2026-06-12 zoom-to-cursor (素のホイール=ズーム, shortcut-settings.md §3 適用例):
  // CanvasStage の wheel listener から「コンテナ client box 内ポインタ位置 (= clientX
  // - rect.left、border-box 基準)」を渡してもらい, ポインタ直下の canvas 座標が
  // zoom 前後で同じ画面位置に留まるよう `viewportAfterZoom` で tx/ty を計算する
  // (= Figma/地図系)。`direction` は +1 でズームイン, -1 でアウト (1 ノッチ = 1 手、
  // +/- キーと同じ CSP 方式ステップ — zoomStepIn/Out、決定2 §6)。
  const handleZoomAtPointer = useCallback(
    (direction: 1 | -1, pointerX: number, pointerY: number): void => {
      const wrapper = canvasScrollRef?.current;
      if (engine === null || !wrapper) return;
      const z = engine.getZoom();
      const nextZoom = direction === 1 ? zoomStepIn(z) : zoomStepOut(z);
      if (nextZoom === z) return;
      const slot = measureSlot(wrapper);
      const before = { ...viewMapper.getTransform(), zoom: engine.displayScaleOf(z) };
      engine.setZoom(nextZoom);
      setViewport(
        viewportAfterZoomRotated(
          before,
          engine.getDisplayScale(),
          pointerX - slot.padX,
          pointerY - slot.padY,
          slot.slotWidth,
          slot.slotHeight,
        ),
      );
    },
    [engine, canvasScrollRef, setViewport, viewMapper],
  );

  // M1-a 分数ズーム: 連続倍率の絶対値指定 + ポインタアンカー版 (M1-c ピンチ用の公開
  // API)。handleZoomAtPointer と同じアンカー方式で、倍率だけ連続値 (clampZoom =
  // `[ZOOM_FIT_MIN, ZOOM_MAX]`) を受ける。clamp 後に現在値と同じなら no-op。
  const handleZoomToAtPointer = useCallback(
    (nextZoomRaw: number, pointerX: number, pointerY: number): void => {
      const wrapper = canvasScrollRef?.current;
      if (engine === null || !wrapper) return;
      const z = engine.getZoom();
      const nextZoom = clampZoom(nextZoomRaw);
      if (nextZoom === z) return;
      const slot = measureSlot(wrapper);
      const before = { ...viewMapper.getTransform(), zoom: engine.displayScaleOf(z) };
      engine.setZoom(nextZoom);
      setViewport(
        viewportAfterZoomRotated(
          before,
          engine.getDisplayScale(),
          pointerX - slot.padX,
          pointerY - slot.padY,
          slot.slotWidth,
          slot.slotHeight,
        ),
      );
    },
    [engine, canvasScrollRef, setViewport, viewMapper],
  );

  // M1-a 入室時フィット (+ 決定3 全体表示ボタン/ショートカット, 決定5 §6): canvas
  // 全景が表示スロット (`<main>` の padding 控除後の内寸) に収まる zoom を適用する。
  // 収まっていれば等倍 1 (= computeFitZoom の仕様、勝手に拡大しない)。viewport は
  // `initialViewport` で中央寄せ (フィットは常に両軸とも canvas<=slot になるので、
  // 決定5「全体表示は中央」を満たす)。
  const applyFitZoom = useCallback((): void => {
    const wrapper = canvasScrollRef?.current;
    if (engine === null || !wrapper) return;
    const slot = measureSlot(wrapper);
    // ドットバイドット時は zoom 1x の見た目サイズが canvas / divisor なので、その大きさで
    // 収まり判定する (fit は利用者 zoom で返る: 収まるなら 1、縮めるなら 表示倍率 × divisor)。
    const div = engine.getDotByDotDivisor();
    const fit = computeFitZoom(
      state.canvas.width / div,
      state.canvas.height / div,
      slot.slotWidth,
      slot.slotHeight,
    );
    engine.setZoom(fit);
    // 全体表示は回転も 0° に戻す (consult 2026-10-03: キーボードの無い iPad の戻し手段。
    // 回転 0 の initialViewport = 中央寄せがそのまま全景を映す)。
    setViewportAndRotation(
      initialViewport(
        state.canvas.width,
        state.canvas.height,
        engine.getDisplayScale(),
        slot.slotWidth,
        slot.slotHeight,
      ),
      0,
    );
  }, [engine, canvasScrollRef, state.canvas.width, state.canvas.height, setViewportAndRotation]);

  // 入室時 (1x, PC も含む) の viewport 初期化 (決定5「入室時 (1x) と全体表示は、
  // canvas が枠より小さい軸は中央寄せ。大きい軸は従来の見え方」): zoom は変えず、
  // 現在の zoom のまま tx/ty だけ中央寄せ計算する。モバイル入室時フィット
  // (isPhoneViewport) は zoom も変える `applyFitZoom` を使う。
  //
  // 呼び出し元は `CanvasStage` (= 表示スロットの実測 `rect` を持つ唯一の場所)。
  // 「engine ready かつ slot 計測済みになった最初の 1 回は初期化、以降の rect
  // 変化はパネル開閉/回転/レイアウト切替による resize として扱い旧中心を保つ
  // (`viewportAfterResize`)」という判定も CanvasStage 側に置く (§9.10 と同様、
  // 「DOM 計測を持つ場所が計測駆動の副作用を持つ」構造)。
  const centerViewportAtCurrentZoom = useCallback((): void => {
    const wrapper = canvasScrollRef?.current;
    if (engine === null || !wrapper) return;
    const slot = measureSlot(wrapper);
    setViewport(
      initialViewport(
        state.canvas.width,
        state.canvas.height,
        engine.getDisplayScale(),
        slot.slotWidth,
        slot.slotHeight,
      ),
    );
  }, [engine, canvasScrollRef, state.canvas.width, state.canvas.height, setViewport]);

  // --- キャンバス表示回転 API (canvas-rotation 段取り 2) ---
  // 回転軸 (slot 座標、content box 左上原点) の真下にある canvas 点を固定したまま
  // 回転量を変える。tx/ty も同時に更新して 1 回の DOM 書き込みで確定する。
  const rotateToAtSlotPoint = useCallback(
    (nextRotation: number, pivotX: number, pivotY: number): void => {
      const wrapper = canvasScrollRef?.current;
      if (engine === null || !wrapper) return;
      const next = normalizeRotation(nextRotation);
      if (next === rotationRef.current) return;
      const slot = measureSlot(wrapper);
      const before = viewMapper.getTransform();
      setViewportAndRotation(
        viewportAfterRotation(before, next, pivotX, pivotY, slot.slotWidth, slot.slotHeight),
        next,
      );
    },
    [engine, canvasScrollRef, viewMapper, setViewportAndRotation],
  );
  const rotateToAtClient = useCallback(
    (nextRotation: number, clientX: number, clientY: number): void => {
      const o = viewMapper.getSlotOrigin();
      rotateToAtSlotPoint(nextRotation, clientX - o.x, clientY - o.y);
    },
    [viewMapper, rotateToAtSlotPoint],
  );
  const rotateToAtSlotCenter = useCallback(
    (nextRotation: number): void => {
      const wrapper = canvasScrollRef?.current;
      if (!wrapper) return;
      const slot = measureSlot(wrapper);
      rotateToAtSlotPoint(nextRotation, slot.slotWidth / 2, slot.slotHeight / 2);
    },
    [canvasScrollRef, rotateToAtSlotPoint],
  );
  const handleRotateLeft = useCallback((): void => {
    rotateToAtSlotCenter(rotationRef.current - ROTATION_STEP_DEG);
  }, [rotateToAtSlotCenter]);
  const handleRotateRight = useCallback((): void => {
    rotateToAtSlotCenter(rotationRef.current + ROTATION_STEP_DEG);
  }, [rotateToAtSlotCenter]);
  const handleRotateReset = useCallback((): void => {
    rotateToAtSlotCenter(0);
  }, [rotateToAtSlotCenter]);

  // layer visibility map (popup hide → 再表示でも保持する単一 source-of-truth)。
  //
  // **初期値は必ず「全表示」**。保存されていた非表示状態はここでは適用せず、
  // ユーザーが確認ダイアログで「非表示のままにする」を選んだときだけ反映する
  // (B-2: 黙って隠すより黙って見せるほうが安全 — 留守中に他の人がそのレイヤーへ
  // 描いている可能性がある)。
  const [layerVisibilityMap, setLayerVisibilityMap] = useState<Record<number, boolean>>(() =>
    initialLayerVisibility(),
  );
  const layerVisibilityStoreRef = useRef(createLayerVisibilityStore());
  // 入室時に 1 回だけ読む (以後 storage を読み直さない = 自分の保存で再発火しない)。
  const [storedHiddenLayers, setStoredHiddenLayers] = useState<readonly number[]>(() =>
    layerVisibilityStoreRef.current.getHidden(state.roomId),
  );
  const keepStoredLayerVisibility = useCallback((): void => {
    setLayerVisibilityMap((prev) => ({ ...prev, ...visibilityMapOf(storedHiddenLayers) }));
    for (const id of storedHiddenLayers) engine?.setLayerVisible(id, false);
    setStoredHiddenLayers([]);
  }, [engine, storedHiddenLayers]);
  const showAllLayers = useCallback((): void => {
    layerVisibilityStoreRef.current.clear(state.roomId);
    setStoredHiddenLayers([]);
  }, [state.roomId]);
  // 👁 トグルの結果を永続化する。updater 内で副作用を起こさない (StrictMode 二重実行で
  // 壊れる) ため effect 側に置く。全表示なら store 側が行ごと削除する。
  useEffect(() => {
    layerVisibilityStoreRef.current.set(state.roomId, layerVisibilityMap);
  }, [state.roomId, layerVisibilityMap]);
  const handleSelectActiveLayer = useCallback((layerId: number): void => {
    setPen((p) => (p.layerId === layerId ? p : { ...p, layerId }));
  }, []);
  // freshest active drawing layer id, read by useImagepaste **only to seed the
  // paste-target at start()** (imagepaste-layer-affordance §3.1 B2). The
  // ImagepasteOverlay inline switch no longer touches the active layer; it
  // mutates useImagepaste's own preview-scoped paste-target (setPasteLayerId).
  // A ref (not pen.layerId in deps) keeps useImagepaste's confirm callback
  // stable, and lets start() seed from the freshest active layer.
  const activeLayerIdRef = useRef<number>(pen.layerId);
  activeLayerIdRef.current = pen.layerId;
  const {
    opacities: layerOpacities,
    control: layerOpacityControl,
    reset: resetLayerOpacities,
  } = useLayerOpacity(engine, pushSystemLine);
  const {
    hub: layerThumbnailHub,
    enabled: layerThumbnailsEnabled,
    setEnabled: onLayerThumbnailsEnabledChange,
    stopped: layerThumbnailsStopped,
  } = useLayerThumbnails(engine, isStrokeActive, panActive);
  const handleToggleLayerVisible = useCallback(
    (layerId: number, visible: boolean): void => {
      setLayerVisibilityMap((prev) =>
        prev[layerId] === visible ? prev : { ...prev, [layerId]: visible },
      );
      engine?.setLayerVisible(layerId, visible);
    },
    [engine],
  );

  // canvas 背景の表示/非表示 (2026-06-20 canvas-background-color §5/B)。レイヤー
  // 👁 と同じ「ローカル個人ビュー」(broadcast しない)。背景は layer ではない (鉄則)
  // ので layerVisibilityMap には混ぜず別 state で持つ。
  //
  // 2026-06-20 CSS 方式 (engine 表示合成から bg を外し canvas は透明に描く方針) へ
  // 移行: 背景可視は **frontend React state が唯一の source-of-truth**。👁 の出し分けは
  // DrawingCanvas が CSS で行う (可視 → backgroundRgb の background-color / 非可視 →
  // チェッカー)。engine の背景可視 setter は no-op 化されたので呼ばない (= 表示に
  // 効かない)。engine 差し替え時の engine からの再 sync も撤去 (= React state が
  // 権威。engine 差し替えで背景可視を勝手に true へ戻さない)。
  const [backgroundVisible, setBackgroundVisible] = useState<boolean>(true);
  const handleToggleBackgroundVisible = useCallback((visible: boolean): void => {
    setBackgroundVisible(visible);
  }, []);

  // canvas 背景 *色* (= 下地色 rgb24)。A-4 ■ swatch (canvas-background-color §4.2)。
  // engine に getBackground() が無いので React がこの色の権威 state を持つ。更新元:
  //   (1) engine 差し替え (新規 join 時) → default 白 0xffffff にリセット。snapshot
  //       経由の persisted bg は (2) の onBackgroundChange seam (meta.background) で
  //       上書きされる (bg 色は engine getter が無いので seam 経由でのみ復元する)。
  //   (2) remote の whiteboard.background-set broadcast / snapshot meta →
  //       SessionProvider.onBackgroundChange (engine 適用後に通知) で更新。これで
  //       非オーナーの ■ も他者の変更を反映する。
  //   (3) オーナー自身の操作 (handleBackgroundColorChange) のローカルプレビュー —
  //       送信が届かない/拒否されたときは (2) の直近の値 (= 確定色) へ戻す
  //       (Bug D 修正、fix/timelapse-background 2026-09-27)。
  const [backgroundRgb, setBackgroundRgb] = useState<number>(DEFAULT_BACKGROUND_RGB);
  // サーバが最後に受理した背景色 (= (2) の直近の値)。`backgroundRgb` はオーナー
  // 自身の未確定プレビューを含み得るので、revert 先として別に保持する。
  const confirmedBackgroundRgbRef = useRef<number>(DEFAULT_BACKGROUND_RGB);
  // 送信済みでまだ echo (= onBackgroundChange 経由の確定) を受けていない背景色。
  // null = 送信待ちなし。ack タイムアウトと WS 切断検知の両方から revert する。
  const pendingBackgroundSendRef = useRef<{
    readonly rgb: number;
    readonly timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const connectionLockedRef = useRef(connectionLocked);
  connectionLockedRef.current = connectionLocked;
  const revertBackgroundPreview = useCallback((): void => {
    if (pendingBackgroundSendRef.current !== null) {
      clearTimeout(pendingBackgroundSendRef.current.timer);
      pendingBackgroundSendRef.current = null;
    }
    setBackgroundRgb(confirmedBackgroundRgbRef.current);
  }, []);
  useEffect(() => {
    if (engine === null) {
      setBackgroundRgb(DEFAULT_BACKGROUND_RGB);
      confirmedBackgroundRgbRef.current = DEFAULT_BACKGROUND_RGB;
      // engine 差し替え (= 新しい部屋) — 前の部屋への送信待ちは無効化する。
      if (pendingBackgroundSendRef.current !== null) {
        clearTimeout(pendingBackgroundSendRef.current.timer);
        pendingBackgroundSendRef.current = null;
      }
    }
  }, [engine]);
  // remote (broadcast / snapshot meta) 由来の bg 色を取り込む seam。
  //   (1) React state を更新 → DrawingCanvas の canvas CSS background-color が即反映
  //       (= 表示は CSS、§8.4 CSS 方式)。
  //   (2) export 用に engine の background property も同期する。受信側は表示が CSS に
  //       なったので、engine.setBackground の目的は「capture/snapshot で焼く bg property の
  //       同期」(= 表示プレビューではない)。alpha は 255 固定 (§4.3)。
  //   (3) 確定色を更新し、送信待ち ack として一致していればタイムアウトを解除する
  //       (Bug D — 自分の送信の echo でも他クライアント発の変更でも「今の確定色」
  //       が更新された事実は同じなので同様に扱う)。
  useEffect(() => {
    const off = onBackgroundChange((rgb) => {
      confirmedBackgroundRgbRef.current = rgb;
      if (pendingBackgroundSendRef.current !== null) {
        clearTimeout(pendingBackgroundSendRef.current.timer);
        pendingBackgroundSendRef.current = null;
      }
      setBackgroundRgb((prev) => (prev === rgb ? prev : rgb));
      engine?.setBackground({ rgb, alpha: 255 });
    });
    return off;
  }, [onBackgroundChange, engine]);

  // 背景色変更の trailing debounce タイマー (canvas-background-color §4.3 最重要)。
  // ColorWheelPalette は wheel ドラッグ中 毎フレーム onColorChange を撃つ。毎回
  // sendBackgroundSet すると broadcast スパム + events.bin 肥大 → 絵ログ再生が壊れる
  // ので、ドラッグが落ち着いた所で 1 発だけ commit する。
  const bgSendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleBackgroundColorChange = useCallback(
    (rgb: number): void => {
      // 再接続中は背景色も変えられない (プレビューも出さない)。
      if (connectionLockedRef.current) {
        revertBackgroundPreview();
        return;
      }
      // (a) 即ローカルプレビュー: React state 更新のみ。これが DrawingCanvas の canvas
      //     CSS background-color に即反映される (= worker/repaint 一切なし = 瞬時)。
      //     2026-06-20 CSS 方式: 表示はもう CSS なので、ここで `engine.setBackground` を
      //     呼んでフル repaint を撃つのは撤去 (ドラッグ中の毎 tick repaint は重い)。
      setBackgroundRgb(rgb);
      // (b) trailing debounce で 1 発 commit。連続発火は最後の値だけが扱われる。
      if (bgSendTimerRef.current !== null) clearTimeout(bgSendTimerRef.current);
      bgSendTimerRef.current = setTimeout(() => {
        bgSendTimerRef.current = null;
        // Bug D ①: WS 未接続 (再接続中等) は `WSClient.send` が `send-before-open`
        // を黙って emit するだけ (どのペイロードだったか分からない) — 送信すら
        // せず即確定色へ戻す。
        if (transport.status.t !== 'open') {
          revertBackgroundPreview();
          return;
        }
        // export 用に engine の background property を最新化する (commit 時のみ、軽い)。
        // capture / camera / snapshot は engine の export 経路で bg を焼くので、property を
        // 古いまま放置すると保存物の背景が古い色になる。alpha は 255 固定 (§4.3)。
        engine?.setBackground({ rgb, alpha: 255 });
        if (pendingBackgroundSendRef.current !== null) {
          clearTimeout(pendingBackgroundSendRef.current.timer);
        }
        // Bug D ②: サーバ拒否 (system.error) は本人 unicast だが元イベントに
        // 紐付く識別子が無いので、個別に判定せず「ack (echo) が時間内に来ない」
        // ことを唯一のシグナルとして revert する (BACKGROUND_SET_ACK_TIMEOUT_MS
        // 定義のコメント参照 — 既知の妥協)。
        const timer = setTimeout(() => {
          if (pendingBackgroundSendRef.current?.rgb === rgb) {
            pendingBackgroundSendRef.current = null;
            setBackgroundRgb(confirmedBackgroundRgbRef.current);
          }
        }, BACKGROUND_SET_ACK_TIMEOUT_MS);
        pendingBackgroundSendRef.current = { rgb, timer };
        sendBackgroundSet(rgb);
      }, BACKGROUND_SET_DEBOUNCE_MS);
    },
    [engine, sendBackgroundSet, transport, revertBackgroundPreview],
  );
  // Bug D ③: WS 切断を検知したら ack タイムアウトを待たず即確定色へ戻す。再接続後
  // (resume の snapshot / welcome) は onBackgroundChange seam が確定色を再同期する
  // ので、ここでは「戻す」だけで済む (再同期そのものは既存経路)。
  useEffect(() => {
    const off = transport.onStatus((status) => {
      if (status.t === 'open') return;
      if (pendingBackgroundSendRef.current !== null) revertBackgroundPreview();
    });
    return off;
  }, [transport, revertBackgroundPreview]);
  // unmount でタイマーを掃除 (= 退室後の遅延 send / revert を防ぐ)。
  useEffect(
    () => () => {
      if (bgSendTimerRef.current !== null) clearTimeout(bgSendTimerRef.current);
      if (pendingBackgroundSendRef.current !== null) {
        clearTimeout(pendingBackgroundSendRef.current.timer);
      }
    },
    [],
  );

  // 背景色変更の perm (canvas-background-color §4.2)。⚠️ canClear を流用しない:
  // canClear は room.clearOpen を含むため、全消し開放部屋で背景変更まで開いてしまう。
  // 背景は hasPerm(Perm.CLEAR) 単独 (clearOpen 非依存) = owner 限定のまま。
  const canSetBackground = hasPerm(state.permissions, Perm.CLEAR);

  // engine ready (DrawingCanvas → SessionProvider + useImagepaste 再 subscribe)。
  const onEngineReady = useCallback(
    (next: CanvasDrawingEngine | null): void => {
      setEngine(next);
      registerEngine(next);
    },
    [registerEngine],
  );

  const imagepaste = useImagepaste({
    transport,
    engine,
    sendImagepasteRequest,
    sendImagepasteConfirm,
    pushSystemLine,
    isConnectionLocked: () => connectionLockedRef.current,
    // §9.7 Phase 2: imagepaste preview の初期位置は「現在の表示範囲 (= pan + zoom
    // 反映) の中央」。canvasScrollRef (= CanvasStage の表示枠 `<main>`、RoomLayout
    // 所有) が渡されていれば、Classic 原典と同じ viewport-center 算出を行う。
    // 2026-09-29 viewport transform 移行 (決定5): 表示範囲の左上 (= 画面 x=0 の
    // 真下にある canvas 座標) は旧 `scrollLeft/zoom` から `-tx/zoom` に変わった
    // (`viewport.ts` と同じ座標規約)。`measureSlot` で padding 控除後の実寸を使う
    // (旧 `clientWidth` そのままより厳密)。未指定 (ref 不在) なら canvas 全体中央
    // (= hook 既定) に倒す。
    getViewportRect: () => {
      const wrapper = canvasScrollRef?.current;
      if (!wrapper) return null;
      const z = zoom <= 0 ? 1 : zoom;
      const slot = measureSlot(wrapper);
      const v = viewportRef.current;
      if (rotationRef.current !== 0) {
        // 回転中: 表示範囲は傾いた四角形。初期位置はその中心 (= 外接矩形の中心) に使うので
        // visibleCanvasQuad の外接矩形を返す。
        const q = visibleCanvasQuad(viewMapper.getTransform(), slot.slotWidth, slot.slotHeight);
        const xs = q.map((p) => p.x);
        const ys = q.map((p) => p.y);
        const left = Math.min(...xs);
        const top = Math.min(...ys);
        return { x: left, y: top, w: Math.max(...xs) - left, h: Math.max(...ys) - top };
      }
      return {
        x: (0 - v.tx) / z,
        y: (0 - v.ty) / z,
        w: slot.slotWidth / z,
        h: slot.slotHeight / z,
      };
    },
    // §3.1 B2: seed the paste-target with the active drawing layer at start().
    // confirm() sends useImagepaste's preview-scoped paste-target, not this.
    getActiveLayerId: () => activeLayerIdRef.current,
  });

  // 範囲の移動・コピー。パネルで選んでいるレイヤー (pen.layerId) は読むだけで、置き先を
  // 選んでも書き戻さない (画像貼り付けの B2 と同じ)。
  const region = useRegionMove({
    transport,
    engine,
    uid: state.uid,
    canvas: { w: state.canvas.width, h: state.canvas.height },
    activeLayerId: pen.layerId,
    active: regionToolActive,
    locked: connectionLocked,
  });

  const canImagepaste = hasPerm(state.permissions, Perm.IMAGEPASTE);
  const canClear = canClearWhiteboard(state.permissions, state.clearOpen) && !connectionLocked;
  const handleClear = useCallback((): void => {
    if (connectionLocked) return;
    setClearConfirmOpen(true);
  }, [setClearConfirmOpen, connectionLocked]);

  // キャプチャ保存 (capture-download-blob-2026-08-13.md)。
  //
  // **`data:` URL は使わない**。旧実装は `toDataURL` の戻りを `<a href>` に載せていて、
  // Chromium の 2MB URL 長制限 (= 描き込むと黙って失敗) と iOS Safari の `data:` 非対応
  // (= iPad で常に失敗) の 2 件を本番 fb で踏んだ。Blob URL 経路に寄せる (§3/§4.1)。
  //
  // 全プラットフォーム共通で blob DL の 1 経路。iOS の Web Share (共有シート) は**採らない**
  // — 理由は `application/captureShare.ts` 冒頭 (実機切り分けと「検知できない」問題)。
  const triggerDownload = useCallback((blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    // 一部ブラウザは document 未接続の要素の click を無視するので append してから click。
    document.body.appendChild(link);
    link.click();
    link.remove();
    // click が URL を消費した後に revoke (TimelapseShareModal と同じ流儀)。
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }, []);
  // 表示 canvas fallback 用の `toBlob` ラッパ (engine が全層 export を持たない / 失敗時)。
  const canvasToBlob = useCallback(
    async (canvas: HTMLCanvasElement): Promise<Blob | null> =>
      await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      }),
    [],
  );
  // どの段で失敗しても**必ず何かが起きる**ようにする (§5 T4)。旧実装は composite が
  // 返らなければ永久に待ち、null なら黙って return していたため、UI 上は「押しても何も
  // 起きない」で終わっていた。timeout → 表示 canvas fallback → それも駄目ならチャット欄に
  // 通知、の順で必ず着地させる。
  // 現在表示中キャンバスの全層合成 Blob を取る共通ヘルパ (元は handleCapture 専用
  // だったロジックを抽出)。engine の全層 export (visibility 無視) を優先し、失敗/
  // 未提供時は表示 canvas の `toBlob` にフォールバックする (§5 T4 と同じ二段構え)。
  // 荒らし復旧 UI 第 1 段階 (vandalism-recovery-ui-2026-09-27.md §2) の「今の絵」
  // プレビュー取得にも同じ経路を使う (= handleCapture との実装差異を避ける)。
  const exportCurrentCanvasBlob = useCallback(async (): Promise<Blob | null> => {
    const exportAll = (
      engine as (CanvasDrawingEngine & { exportAllLayersBlob?: () => Promise<Blob | null> }) | null
    )?.exportAllLayersBlob;
    let blob: Blob | null = null;
    if (typeof exportAll === 'function') {
      try {
        blob = await withTimeout(exportAll.call(engine), CAPTURE_EXPORT_TIMEOUT_MS);
      } catch {
        blob = null;
      }
    }
    if (blob === null) {
      const canvas = canvasRef.current;
      if (canvas !== null) {
        try {
          blob = await withTimeout(canvasToBlob(canvas), CAPTURE_EXPORT_TIMEOUT_MS);
        } catch {
          blob = null;
        }
      }
    }
    return blob;
  }, [engine, canvasToBlob]);

  const handleCapture = useCallback((): void => {
    void (async (): Promise<void> => {
      const blob = await exportCurrentCanvasBlob();
      if (blob === null) {
        pushSystemLine(CAPTURE_FAILED_MESSAGE);
        return;
      }
      try {
        triggerDownload(blob, captureFileName(new Date()));
      } catch {
        pushSystemLine(CAPTURE_FAILED_MESSAGE);
      }
    })();
  }, [exportCurrentCanvasBlob, triggerDownload, pushSystemLine]);

  // --- pen / color / width handlers ---
  const onSelectPen = useCallback(
    (
      kind:
        | 'brush'
        | 'flat'
        | 'marker'
        | 'texture'
        | 'tone'
        | 'airbrush'
        | 'crayon'
        | 'pastel'
        | 'dot',
    ): void => {
      setRotateToolActive(false);
      setPen((p) => ({ ...p, pen: kind }));
    },
    [setRotateToolActive],
  );
  // texture preset 選択 (texture-brush-design.md §11 step 3, Modern 2階層 UI)。
  // pen.texturePreset を書く。penParamsFor が pen='texture' のときだけ wire/engine に
  // 流すので、preset は texture を選んでいる間だけ効く。texture 以外でも保持しておけば
  // (= setPen で常に書く)、texture に戻したとき直前選択が復活する。
  const onSelectTexturePreset = useCallback(
    (preset: TexturePreset): void =>
      setPen((p) => (p.texturePreset === preset ? p : { ...p, texturePreset: preset })),
    [],
  );
  // トーン番手選択 (texture-brush-design.md §12.2 段階1, Modern「トーン」カテゴリ)。
  // pen.toneDensity (0..1) を書く。penParamsFor が pen='tone' のときだけ wire/engine に
  // 流すので、番手は tone を選んでいる間だけ効く。tone 以外でも保持しておけば (= setPen で
  // 常に書く)、tone に戻したとき直前 density が復活する。0..1 に clamp して保持する。
  const onToneDensityChange = useCallback((density: number): void => {
    const d = Math.max(0, Math.min(1, density));
    setPen((p) => (p.toneDensity === d ? p : { ...p, toneDensity: d }));
  }, []);
  // エアブラシ grain/density (airbrush-design-2026-08-15.md §1.3 / §6)。tone 番手と
  // 同型: pen.airbrushGrain/airbrushDensity を書く。penParamsFor が pen='airbrush' の
  // ときだけ wire/engine に流すので、airbrush を選んでいる間だけ効く。airbrush 以外
  // でも保持しておけば (= setPen で常に書く)、airbrush に戻したとき直前値が復活する。
  const onAirbrushGrainChange = useCallback((grain: number): void => {
    const g = Math.max(0, Math.min(3, Math.round(grain)));
    setPen((p) => (p.airbrushGrain === g ? p : { ...p, airbrushGrain: g }));
  }, []);
  const onAirbrushDensityChange = useCallback((density: number): void => {
    const d = Math.max(0, Math.min(1, density));
    setPen((p) => (p.airbrushDensity === d ? p : { ...p, airbrushDensity: d }));
  }, []);
  // クレヨン/パステルの目の粗さ (phase11-light-pens-2026-08-21.md §2.6 / §6)。airbrush
  // grain と同型: pen.crayonGrain/pastelGrain を書く。penParamsFor が該当ペンのときだけ
  // wire/engine に流すので、grain はそのペンを選んでいる間だけ効く。それ以外でも保持
  // しておけば (= setPen で常に書く)、戻したとき直前値が復活する。0..2 の整数に clamp。
  const onCrayonGrainChange = useCallback((grain: number): void => {
    const g = Math.max(0, Math.min(2, Math.round(grain)));
    setPen((p) => (p.crayonGrain === g ? p : { ...p, crayonGrain: g }));
  }, []);
  const onPastelGrainChange = useCallback((grain: number): void => {
    const g = Math.max(0, Math.min(2, Math.round(grain)));
    setPen((p) => (p.pastelGrain === g ? p : { ...p, pastelGrain: g }));
  }, []);
  // トーンのドット間隔 (pitch, phase11-light-pens-2026-08-21.md §4.5-§4.7)。toneDensity
  // と同型: pen.tonePitch を書く。penParamsFor が pen='tone' のときだけ wire/engine に
  // 流すので、pitch は tone を選んでいる間だけ効く。UI は 8/16/32/64 の segmented から
  // しか呼ばれないが、念のため 1 以上の整数に clamp する (protocol `pitch` は int min1)。
  const onTonePitchChange = useCallback((pitch: number): void => {
    const p2 = Math.max(1, Math.round(pitch));
    setPen((p) => (p.tonePitch === p2 ? p : { ...p, tonePitch: p2 }));
  }, []);
  // ドットの間隔 (spacing, phase11-light-pens-2026-08-21.md §3.4/§3.5/§6)。protocol の
  // `dot` arm 値域 (1..200) に clamp する (grain 系と同じ防御的 clamp)。
  const onDotSpacingChange = useCallback((spacing: number): void => {
    const s = Math.max(1, Math.min(200, Math.round(spacing)));
    setPen((p) => (p.dotSpacing === s ? p : { ...p, dotSpacing: s }));
  }, []);
  // キーボード P/B (= brush 直接選択) / E (= eraser 直接選択)。ToolPanel の eraser
  // toggle (onToggleEraser) とは別経路で、ClassicLayout のキーボード handler 原典の
  // 「setPen({pen:'brush'})」「setPen({pen:'eraser'})」をそのまま移設する。
  const onSelectBrush = useCallback((): void => {
    setRotateToolActive(false);
    setPen((p) => ({ ...p, pen: 'brush' }));
  }, [setRotateToolActive]);
  const onSelectEraserDirect = useCallback((): void => {
    setRotateToolActive(false);
    setPen((p) => ({ ...p, pen: 'eraser' }));
  }, [setRotateToolActive]);
  // キーボード M (= マーカー bucket toggle)。bucket 内 (flat/marker) なら相方へ、
  // bucket 外 (brush/eraser) なら直前 bucket メンバ (lastMarkerKindRef) を選ぶ。
  // closure-safe に最新の lastMarkerKind を読むため ref を使う (ClassicLayout 原典踏襲)。
  const onMarkerBucketToggle = useCallback((): void => {
    setRotateToolActive(false);
    setPen((p) => {
      if (p.pen === 'flat' || p.pen === 'marker') {
        return { ...p, pen: p.pen === 'flat' ? 'marker' : 'flat' };
      }
      return { ...p, pen: lastMarkerKindRef.current };
    });
  }, [setRotateToolActive]);
  // CSP流 循環ショートカット (2026-08-21): `tool.pen.*` 個別ペン action の汎用 mutator。
  // onSelectBrush/onSelectEraserDirect と同じ setPen パターンを任意の StrokePen に
  // 一般化しただけ (= 挙動は等価)。keyboard hook からのみ呼ばれる想定。
  const onSelectPenRef = useCallback(
    (nextPen: StrokePen): void => {
      setRotateToolActive(false);
      setPen((p) => ({ ...p, pen: nextPen }));
    },
    [setRotateToolActive],
  );
  // `tool.tab.*` タブ action: そのタブで最後に使った pen を選ぶ (lastUsedPenForTabRef、
  // stale closure 回避のため ref 経由で読む、lastMarkerKindRef と同じ流儀)。
  const onSelectTabRef = useCallback(
    (tab: PenTabId): void => onSelectPenRef(lastUsedPenForTabRef.current[tab]),
    [onSelectPenRef],
  );
  const [paletteSlots, setPaletteSlots] = useState<ReadonlyArray<number>>(LEGACY_PALETTE);
  const [selectedPaletteIdx, setSelectedPaletteIdx] = useState<number>(0);
  const onColorChange = useCallback(
    (c: number): void => {
      setPen((p) => ({ ...p, color: c }));
      setPaletteSlots((prev) => {
        if (selectedPaletteIdx < 0 || selectedPaletteIdx >= prev.length) return prev;
        if (prev[selectedPaletteIdx] === c) return prev;
        const next = prev.slice();
        next[selectedPaletteIdx] = c;
        return next;
      });
    },
    [selectedPaletteIdx],
  );
  // スポイト色pick 専用の色採用経路 (spuit-sample-modes-2026-06-22 §2.1、consult
  // 2026-06-27、モードB 対称トグル)。通常の onColorChange (パレット / カラーホイール /
  // ARGB エディタ) と分離するのが要点: ツール復帰はスポイトで **色を拾ったとき** にだけ
  // 起き、手動の色変更では起きてはならない。
  //
  //   - モードB ('layer') で色 (α>0) を拾ったとき、今が消しゴムなら直前ペン
  //     (lastNonEraserPen、無ければ既定 = seed 'brush') へ復帰してから色を採用する。
  //     今が既にペンならツール変更なし (色採用のみ)。
  //   - A ('display') / C ('layer-bg') はツールに一切触れない (色採用のみ、従来どおり)。
  //
  // α0 (消しゴムへ切替) 経路は別 (onSelectEraserDirect = onSpuitEraser)。本経路は
  // coalescer の onColor (= 色を拾えた pick) からのみ呼ばれる。
  const onSpuitColor = useCallback(
    (c: number): void => {
      if (spuitSampleMode === 'layer') {
        setPen((p) => (p.pen === 'eraser' ? { ...p, pen: lastNonEraserPen } : p));
      }
      onColorChange(c);
    },
    [spuitSampleMode, lastNonEraserPen, onColorChange],
  );
  const onPaletteSlotSelect = useCallback(
    (idx: number): void => {
      if (idx < 0 || idx >= paletteSlots.length) return;
      setSelectedPaletteIdx(idx);
      const c = paletteSlots[idx];
      if (c !== undefined) setPen((p) => ({ ...p, color: c }));
    },
    [paletteSlots],
  );
  /**
   * α feedback (2026-06-13): 「パレットを初期16色に戻す」機能。paletteSlots は
   * localStorage 永続化していない (= セッション内のみ) ので、リロードすれば初期化される
   * が「セッション中に戻す手段が無い」という要望。呼び出し側 (Classic PalettePanel /
   * Modern ModernColorPalette) は誤クリック防止のワンクッション (ConfirmDialog) を挟んで
   * から本 callback を呼ぶ想定。
   *
   * 設計判断 (選択中 slot / ペン色の扱い):
   * - `selectedPaletteIdx` (選択中 slot) 自体は維持する。リセットは「色の中身」を戻す
   *   操作であって「今どの slot を触っているか」という選択状態とは無関係なため、選択を
   *   飛ばすと直後の操作 (太さ調整・描画開始等) の文脈が変わってしまいユーザが混乱する。
   * - 選択中 slot の**表示色**は `pen.color` (= 動的、PalettePanel/ModernColorPalette
   *   共に選択 cell は `paletteSlots[idx]` ではなく `pen.color` を描画する既存仕様) なので、
   *   `paletteSlots` だけ戻して `pen.color` を放置すると「非選択 15 色は瞬時に初期化される
   *   のに選択中の 1 色だけ古いカスタム色のまま残る」という見た目の不整合が起きる。
   *   これを避けるため、選択中 slot の初期色 (`LEGACY_PALETTE[selectedPaletteIdx]`) を
   *   `pen.color` にも同時反映し、16 色全てが見た目どおり初期状態に揃うようにする
   *   (= `onPaletteSlotSelect` が「slot 選択時に pen.color をその slot 色に倒す」のと
   *   同じ考え方をリセットにも適用しただけ)。
   */
  const onPaletteReset = useCallback((): void => {
    setPaletteSlots(LEGACY_PALETTE);
    setPen((p) => {
      const idx = selectedPaletteIdx;
      if (idx < 0 || idx >= LEGACY_PALETTE.length) return p;
      const defaultColor = LEGACY_PALETTE[idx];
      if (defaultColor === undefined || defaultColor === p.color) return p;
      return { ...p, color: defaultColor };
    });
  }, [selectedPaletteIdx]);
  const onWidthChange = useCallback(
    (w: number): void => setWidthBuckets((b) => applyWidthToActiveBucket(b, pen.pen, w)),
    [pen.pen],
  );
  const onAlphaChange = useCallback((a: number): void => setPen((p) => ({ ...p, alpha: a })), []);
  const onToggleEraser = useCallback((): void => {
    setRotateToolActive(false);
    if (pen.pen === 'eraser') {
      setPen((p) => ({ ...p, pen: lastNonEraserPen }));
    } else {
      setLastNonEraserPen(pen.pen);
      setPen((p) => ({ ...p, pen: 'eraser' }));
    }
  }, [pen.pen, lastNonEraserPen, setRotateToolActive]);
  const onPenWidthValueChange = useCallback(
    (selectedIndex: number, newWidth: number): void => {
      const setActivePenWidths = isEraserActive(pen.pen) ? setEraserPenWidths : setDrawPenWidths;
      setActivePenWidths((prev) => {
        if (selectedIndex < 0 || selectedIndex >= prev.length) return prev;
        const next = prev.slice();
        next[selectedIndex] = newWidth;
        return next;
      });
    },
    [pen.pen],
  );
  const onPenWidthCellClick = useCallback(
    (index: number): void => {
      const eraser = isEraserActive(pen.pen);
      const widths = eraser ? eraserPenWidths : drawPenWidths;
      if (index < 0 || index >= widths.length) return;
      if (eraser) setEraserSelectedPenWidthIndex(index);
      else setDrawSelectedPenWidthIndex(index);
      const w = widths[index];
      if (w !== undefined) setWidthBuckets((b) => applyWidthToActiveBucket(b, pen.pen, w));
    },
    [pen.pen, eraserPenWidths, drawPenWidths],
  );

  // --- mask handlers ---
  const onMaskSlotClick = useCallback(
    (slot: 0 | 1): void => {
      const cur = slot === 0 ? slot1 : slot2;
      const next: MaskSlotValue = cur === null ? pen.color : null;
      const nextSlot1 = slot === 0 ? next : slot1;
      const nextSlot2 = slot === 1 ? next : slot2;
      if (slot === 0) setSlot1(next);
      else setSlot2(next);
      setPen((p) => ({ ...p, mask: slotsToMask(nextSlot1, nextSlot2, maskTypeUI) }));
    },
    [slot1, slot2, maskTypeUI, pen.color],
  );
  const onMaskTypeToggle = useCallback((): void => {
    const next: MaskTypeUI = maskTypeUI === 'normal' ? 'inverse' : 'normal';
    setMaskTypeUI(next);
    setPen((p) => ({ ...p, mask: slotsToMask(slot1, slot2, next) }));
  }, [maskTypeUI, slot1, slot2]);

  return {
    pen,
    paletteSlots,
    selectedPaletteIdx,
    activePenWidths,
    activeSelectedPenWidthIndex,
    onSelectPen,
    texturePreset: pen.texturePreset,
    onSelectTexturePreset,
    toneDensity: pen.toneDensity,
    onToneDensityChange,
    airbrushGrain: pen.airbrushGrain,
    onAirbrushGrainChange,
    airbrushDensity: pen.airbrushDensity,
    onAirbrushDensityChange,
    crayonGrain: pen.crayonGrain,
    onCrayonGrainChange,
    pastelGrain: pen.pastelGrain,
    onPastelGrainChange,
    tonePitch: pen.tonePitch,
    onTonePitchChange,
    dotSpacing: pen.dotSpacing,
    onDotSpacingChange,
    onToggleEraser,
    onSelectBrush,
    onSelectEraserDirect,
    onMarkerBucketToggle,
    onSelectPenRef,
    onSelectTabRef,
    shortcutCyclePositions,
    onShortcutCycleIndexChange,
    onWidthChange,
    onPenWidthCellClick,
    onPenWidthValueChange,
    onColorChange,
    onSpuitColor,
    onAlphaChange,
    onPaletteSlotSelect,
    onPaletteReset,
    slot1,
    slot2,
    maskTypeUI,
    onMaskSlotClick,
    onMaskTypeToggle,
    currentPressure,
    onPressureChange,
    stabilizerLevel,
    onStabilizerChange,
    aaEnabled,
    onAaChange,
    dotByDotMode,
    onDotByDotModeChange,
    dotByDotActive,
    brushCursorType,
    onBrushCursorTypeChange,
    showOwnCustomCursor,
    onShowOwnCustomCursorChange,
    spuitSampleMode,
    onSpuitSampleModeChange,
    penOnlyMode,
    setPenOnlyMode,
    handlePenAutoEnable,
    panActive,
    setPanActive,
    setStrokeActive,
    isStrokeActive,
    spuitMode,
    setSpuitMode,
    onSpuitCommit,
    registerSpuitCommitHandler,
    layerVisibilityMap,
    storedHiddenLayers,
    keepStoredLayerVisibility,
    showAllLayers,
    handleSelectActiveLayer,
    handleToggleLayerVisible,
    layerOpacities,
    layerOpacityControl,
    resetLayerOpacities,
    layerThumbnailHub,
    layerThumbnailsEnabled,
    onLayerThumbnailsEnabledChange,
    layerThumbnailsStopped,
    backgroundVisible,
    handleToggleBackgroundVisible,
    backgroundRgb,
    handleBackgroundColorChange,
    canSetBackground,
    nameLabelMode,
    nameLabels,
    zoom,
    displayScale,
    flipX,
    handleToggleFlip,
    handleToggleNameLabelMode,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    handleZoomAtPointer,
    handleZoomToAtPointer,
    applyFitZoom,
    viewportRef,
    viewportLayerRef,
    setViewport,
    viewMapper,
    centerViewportAtCurrentZoom,
    rotation,
    getRotation,
    onRotationChange,
    rotateToAtClient,
    rotateToAtSlotCenter,
    handleRotateLeft,
    handleRotateRight,
    handleRotateReset,
    rotateToolActive,
    setRotateToolActive,
    onSelectRotateTool,
    regionToolActive,
    onSelectRegionTool,
    showRegionNewBadge: !regionNewBadgeSeen,
    region,
    engine,
    canvasRef,
    onEngineReady,
    imagepaste,
    canImagepaste,
    canClear,
    handleClear,
    handleCapture,
    getCurrentCanvasBlob: exportCurrentCanvasBlob,
  };
};
